import { normalizeRTSP, clearRTSPConfig, rtspPayload } from "../rtspTarget.mjs";
import { normalizeSEI, seiPayload, clearSEIConfig } from "../sei.mjs";
import { normalizeWhip, whipPayload } from "../whip.mjs";
import { flywheelCaptionValues } from "../flywheelCaption.mjs";
import { normalizeLlHls, llHlsPayload } from "../llHls.mjs";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { alpha } from "@mui/material/styles";
import DefaultPropsProvider from "@mui/material/DefaultPropsProvider";
import { dialogSurfaceColors } from "../theme";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import {
  createDefaultVideoParams,
  DEFAULT_VIDEO_BITRATE,
  DESKTOP_DEFAULT_VIDEO_BITRATE,
  VideoParamsFormValues,
  VideoParamsH264,
  VideoParamsVp8,
  VideoParamsVp9,
  VIDEO_CODEC_OPTIONS
} from "./VideoParams";
import { useAppContext } from "../context/AppContext";
import FileInputParams, { createDefaultFileInputParams, FileInputParamsFormValues } from "./FileInputParams";
import SrtInputParams, { createDefaultSrtInputParams, SrtInputParamsFormValues } from "./SrtInputParams";
import RtspInputParams, { createDefaultRtspInputParams, RtspInputParamsFormValues } from "./RtspInputParams";
import UdsInputParams, { createDefaultUdsInputParams, UdsInputParamsFormValues } from "./UdsInputParams";
import StreamOutputs from "./StreamOutputs";
import PatternParams from "./PatternParams";
import { parsePattern, patternValue, validatePattern, visiblePatternUrl, withHiddenPatternKlv } from "../patternSource.mjs";
import { frameRateFraction, frameRateText, frameRateError } from "../framerate.mjs";
import FlywheelParams, { createDefaultFlywheelValues, type FlywheelFormValues } from "./FlywheelParams";
import HostMediaFolder from "./HostMediaFolder";
import type { StreamTargetForm } from "../types/streamTargets";
import { isDesktopShellEnvironment } from "../utils/desktopShell";
import { openExternalPath } from "../utils/externalLinks";
import { validateHostlessStreamSourceUrl } from "../utils/streamSourceUrl";
import { syntheticKlvNativeTemplate, syntheticKlvTemplateDocument } from "../syntheticKlvTemplate.mjs";

type AuthorizedFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type VideoStreamConfig = Record<string, unknown>;

type StreamVideoSettings = {
  streams: VideoStreamConfig[];
};

type StreamSrtSettings = Record<string, unknown>;
type StreamUdsSettings = Record<string, unknown>;
type StreamRtspSettings = Record<string, unknown>;
type StreamFileSettings = Record<string, unknown>;
type StreamPcapSettings = Record<string, unknown>;
type PcapFormValues = { file: string; hostMediaDrive: string; sourceIp: string; destinationIp: string; sourcePort: number | ""; destinationPort: number | ""; loop: boolean };
type PcapUDPFlow = { sourceIp: string; destinationIp: string; sourcePort: number; destinationPort: number; packets: number; payloadBytes: number };
type DesktopCaptureImportResult = { path?: string; size?: number };
type DesktopCaptureSelectionResult = { sourcePath?: string; name?: string; size?: number };
type DesktopCaptureBridge = {
  go?: { main?: { DesktopApp?: {
    SelectCaptureFile?: () => Promise<DesktopCaptureSelectionResult>;
    ImportCaptureFile?: (sourcePath: string) => Promise<DesktopCaptureImportResult>;
    SelectMediaFile?: () => Promise<string>;
  } } };
};
type StreamProcessorEngine = {
  id?: string;
  type: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
};
type StreamProcessor = {
  id: string;
  engines?: StreamProcessorEngine[];
};
type StreamTargetEngine = {
  id?: string;
  type: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
};
type StreamTarget = {
  id: string;
  inputType?: string;
  inputId?: string;
  engines?: StreamTargetEngine[];
};

type StreamMode = "play" | "recast" | "playAndRecast";

export type StreamDialogStream = {
  id: string;
  sensor: string;
  platformId?: string;
  platformName?: string;
  type: string;
  url: string;
  networkInterface?: string;
  description?: string;
  payloadType?: string;
  active: boolean;
  autoStart: boolean;
  passthrough: boolean;
  live?: StreamVideoSettings | null;
  mode?: StreamMode;
  processors?: StreamProcessor[] | null;
  targets?: StreamTarget[] | null;
  timeout: number;
  offlineAfter: number;
  bufferSize: number;
  lowLatency?: boolean;
  cacheMs?: number;
  addPlatformMetadata?: boolean;
  srt?: StreamSrtSettings | null;
  uds?: StreamUdsSettings | null;
  rtsp?: StreamRtspSettings | null;
  file?: StreamFileSettings | null;
  pcap?: StreamPcapSettings | null;
};

type StreamFormValues = {
  sensor: string;
  platformId: string;
  type: string;
  url: string;
  networkInterface: string;
  description: string;
  payloadType: string;
  active: boolean;
  autoStart: boolean;
  passthrough: boolean;
  video: VideoParamsFormValues;
  srt: SrtInputParamsFormValues;
  uds: UdsInputParamsFormValues;
  rtsp: RtspInputParamsFormValues;
  file: FileInputParamsFormValues;
  pcap: PcapFormValues;
  timeout: number | "";
  offlineAfter: number | "";
  bufferSizeKb: number | "";
  lowLatency: boolean;
  cacheMs: number | "";
  addPlatformMetadata: boolean;
  targets: StreamTargetForm[];
  processors: StreamProcessor[];
  flywheel: FlywheelFormValues;
};

type PlatformOption = {
  id: string;
  name: string;
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

type StreamFormTab = "general" | "stream" | "file" | "pcap" | "video" | "srt" | "uds" | "rtsp" | "flywheel" | "outputs";

type StreamFormDialogProps = {
  open: boolean;
  onClose: () => void;
  editingStream: StreamDialogStream | null;
  streams: StreamDialogStream[];
  platformOptions: PlatformOption[];
  isPlatformsLoading: boolean;
  isPlatformsError: boolean;
  platformsError: unknown;
  networkInterfaceOptions: string[];
  isNetworkInterfacesLoading: boolean;
  loadNetworkInterfaces: (currentValue?: string) => Promise<void>;
  authorizedFetch: AuthorizedFetch;
  resolveApiPath: (path: string) => string;
  accessToken?: string | null;
  invalidateStreams: () => void;
  onShowSnackbar: (snackbar: SnackbarState) => void;
  isEditingStreamRunning: boolean;
  readOnly?: boolean;
};

const STREAM_TYPE_OPTIONS = [
  { value: "udp", label: "UDP", tooltip: "User Datagram Protocol" },
  { value: "srt", label: "SRT", tooltip: "Secure Reliable Transport" },
  { value: "rtsp", label: "RTSP", tooltip: "Real-Time Streaming Protocol" },
  { value: "uds", label: "UDS", tooltip: "Unix Domain Socket" },
  { value: "file", label: "FILE", tooltip: "Local file" },
  { value: "pattern", label: "Pattern", tooltip: "Generate video and optional KLV" },
  { value: "pcap", label: "PCAP", tooltip: "UDP payload replay from PCAP or PCAPNG" },
  { value: "rtp", label: "RTP", tooltip: "Real-time Transport Protocol" },

] as const;

const TYPE_URL_PREFIX: Record<(typeof STREAM_TYPE_OPTIONS)[number]["value"], string> = {
  udp: "udp",
  srt: "srt",
  rtsp: "rtsp",
  uds: "uds",
  file: "file",
  pcap: "pcap",
 pattern:"pattern",
  rtp: "rtp",
};

const getTypeProtocolPrefix = (type: string): string | null => {
  const normalizedType = (type ?? "").trim().toLowerCase();
  if (!normalizedType) {
    return null;
  }

  const prefixCandidate =
    normalizedType in TYPE_URL_PREFIX
      ? TYPE_URL_PREFIX[normalizedType as keyof typeof TYPE_URL_PREFIX]
      : normalizedType;
  const normalizedPrefix = prefixCandidate.trim().toLowerCase();
  if (!normalizedPrefix) {
    return null;
  }

  return normalizedPrefix.endsWith("://") ? normalizedPrefix : `${normalizedPrefix}://`;
};

const buildFileUrlFromPath = (path: string): string => {
  const trimmed = path.trim();
  if (!trimmed) {
    return "";
  }

  const normalized = trimmed.replace(/\\/g, "/");
  if (/^[a-zA-Z]:\//.test(normalized)) {
    return `file:///${normalized}`;
  }
  if (normalized.startsWith("//")) {
    return `file:${normalized}`;
  }
  if (normalized.startsWith("/")) {
    return `file://${normalized}`;
  }
  return `file://${normalized}`;
};

const stripTypeProtocolFromUrl = (url: string, protocol: string | null): string => {
  if (!url) {
    return "";
  }
  if (!protocol) {
    return url;
  }

  const lowerProtocol = protocol.toLowerCase();
  const lowerUrl = url.toLowerCase();
  const remainder = lowerUrl.startsWith(lowerProtocol) ? url.slice(lowerProtocol.length) : url;
  return remainder.replace(/^\s+/, "");
};

const normalizeStreamMode = (mode?: string | null): StreamMode => {
  const normalized = (mode ?? "").trim().toLowerCase();
  if (normalized === "recast") {
    return "recast";
  }
  if (normalized === "playandrecast") {
    return "playAndRecast";
  }
  return "play";
};

const collectRequiredValidationMessages = (value: unknown): string[] => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /\brequired\b/i.test(trimmed) ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectRequiredValidationMessages(entry));
  }
  if (value && typeof value === "object") {
    return Object.values(value).flatMap((entry) => collectRequiredValidationMessages(entry));
  }
  return [];
};

const STREAM_MODE_OPTIONS: { value: StreamMode; label: string }[] = [
  { value: "play", label: "Player" },
  { value: "recast", label: "Recaster" },
  { value: "playAndRecast", label: "Play and Recast" }
];

const PAYLOAD_TYPE_OPTIONS = [
  { value: "mpegts", label: "MPEG-TS" },
  { value: "data", label: "Data" }
] as const;

const DEFAULT_TIMEOUT = 4000;
const DEFAULT_OFFLINE_AFTER = 20000;
const BYTES_PER_BUFFER_KB = 1024;
const DEFAULT_BUFFER_SIZE_BYTES = 4 * 1024 * 1024;
const DEFAULT_BUFFER_SIZE_KB = DEFAULT_BUFFER_SIZE_BYTES / BYTES_PER_BUFFER_KB;
const MAX_BUFFER_SIZE_BYTES = 86400000;
const MAX_BUFFER_SIZE_KB = MAX_BUFFER_SIZE_BYTES / BYTES_PER_BUFFER_KB;
const BUFFER_SIZE_TOOLTIP =
  "Default/minimum: 4,096 KB = 4 MiB (4,194,304 bytes).";
const DEFAULT_CACHE_MS = 400;
const MIN_CACHE_MS = 1;
const MAX_CACHE_MS = 10000;
const CACHE_TOOLTIP =
  "Playback cache in milliseconds. Larger values improve smoothness but add startup and end-to-end delay.";

const bufferBytesToKb = (bytes: number): number => bytes / BYTES_PER_BUFFER_KB;
const bufferKbToBytes = (kilobytes: number): number =>
  Math.round(kilobytes * BYTES_PER_BUFFER_KB);
const DEFAULT_PAYLOAD_TYPE = PAYLOAD_TYPE_OPTIONS[0].value;
const NO_SCALING_OPTION = "No scaling";
const NO_PLATFORM_KEY = "__no_platform__";

const isMulticastUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  try {
    const url = new URL(trimmed);
    const hostname = url.hostname;
    if (!hostname) {
      return false;
    }

    if (!/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return false;
    }

    const octets = hostname.split(".").map((part) => Number.parseInt(part, 10));
    if (octets.length !== 4 || octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
      return false;
    }

    return octets[0] >= 224 && octets[0] <= 239;
  } catch {
    return false;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getPlatformKey = (platformId?: string, platformName?: string): string => {
  const trimmedId = (platformId ?? "").trim();
  if (trimmedId) {
    return trimmedId.toLowerCase();
  }
  const trimmedName = (platformName ?? "").trim();
  if (trimmedName) {
    return trimmedName.toLowerCase();
  }
  return NO_PLATFORM_KEY;
};

const isValidIpv4 = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const octets = value.split(".");
  if (octets.length !== 4) {
    return false;
  }
  return octets.every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) {
      return false;
    }
    const numeric = Number(octet);
    return numeric >= 0 && numeric <= 255;
  });
};

const isValidIpv6 = (value: string): boolean => {
  if (!value.includes(":")) {
    return false;
  }
  try {
    const candidate = new URL(`http://[${value}]/`);
    return candidate.hostname === value;
  } catch {
    return false;
  }
};

const isValidDomainName = (value: string): boolean => {
  const DOMAIN_REGEX = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
  return DOMAIN_REGEX.test(value);
};

const isValidSingleLabelHostname = (value: string): boolean => {
  const SINGLE_LABEL_REGEX = /^[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
  return SINGLE_LABEL_REGEX.test(value);
};

const isValidHost = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.trim();
  if (!normalized) {
    return false;
  }
  if (normalized.toLowerCase() === "localhost") {
    return true;
  }
  if (isValidIpv4(normalized) || isValidIpv6(normalized)) {
    return true;
  }
  if (isValidDomainName(normalized)) {
    return true;
  }
  return isValidSingleLabelHostname(normalized);
};

const decodeLicenseOptions = (token: string): string[] => {
  if (!token) {
    return [];
  }
  const parts = token.split(".");
  if (parts.length < 2) {
    return [];
  }
  const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "===".slice((normalized.length + 3) % 4);
  try {
    const decoded = atob(padded);
    const payload = JSON.parse(decoded) as { product?: { options?: unknown } };
    const options = payload?.product?.options;
    if (!Array.isArray(options)) {
      return [];
    }
    return options.filter((option): option is string => typeof option === "string");
  } catch (error) {
    console.warn("Failed to decode license token options", error);
    return [];
  }
};

const WINDOWS_NAMED_PIPE_PREFIX = /^\\\\\.\\pipe\\/i;
const WINDOWS_DRIVE_PATH_PREFIX = /^[a-z]:[\\/]/i;
const UDS_NAME_PATTERN = /^[a-z0-9._-]+(?:[\\/][a-z0-9._-]+)*$/i;

const isValidUdsSocketPathValue = (value: string): boolean => {
  let trimmed = value.trim();
  if (!trimmed || trimmed.includes("\u0000")) {
    return false;
  }
  const udsPrefix = "uds://";
  if (trimmed.toLowerCase().startsWith(udsPrefix)) {
    trimmed = trimmed.slice(udsPrefix.length).trim();
  }
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith("/")) {
    return trimmed.length <= 107;
  }
  if (WINDOWS_NAMED_PIPE_PREFIX.test(trimmed)) {
    return true;
  }
  if (WINDOWS_DRIVE_PATH_PREFIX.test(trimmed)) {
    return true;
  }
  return UDS_NAME_PATTERN.test(trimmed);
};

const parseResolutionValue = (record: Record<string, unknown>, fallback: string): string => {
  const resolutionCandidate =
    (record as Record<string, unknown> & { Resolution?: unknown }).resolution ?? record["Resolution"];
  const fallbackResolution = fallback || NO_SCALING_OPTION;
  if (typeof resolutionCandidate === "string" && resolutionCandidate.trim()) {
    const trimmed = resolutionCandidate.trim();
    if (trimmed === "-1x-1") {
      return fallbackResolution;
    }
    return trimmed;
  }

  if (isRecord(resolutionCandidate)) {
    const widthCandidate = resolutionCandidate.width ?? resolutionCandidate["Width"];
    const heightCandidate = resolutionCandidate.height ?? resolutionCandidate["Height"];
    if (
      typeof widthCandidate === "number" &&
      Number.isFinite(widthCandidate) &&
      typeof heightCandidate === "number" &&
      Number.isFinite(heightCandidate) &&
      widthCandidate > 0 &&
      heightCandidate > 0
    ) {
      return `${Math.round(widthCandidate)}x${Math.round(heightCandidate)}`;
    }
  }

  const width = record.width ?? record["Width"];
  const height = record.height ?? record["Height"];
  if (
    typeof width === "number" &&
    Number.isFinite(width) &&
    typeof height === "number" &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0
  ) {
    return `${Math.round(width)}x${Math.round(height)}`;
  }
  return fallbackResolution;
};

const parseNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

const pickFirst = (record: Record<string, unknown>, keys: string[]): unknown => {
  for (const key of keys) {
    if (key in record) {
      return record[key];
    }
  }
  return undefined;
};

const parseCodec = (value: unknown, fallback: VideoParamsFormValues["codec"]): VideoParamsFormValues["codec"] => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["h264", "vp8", "vp9"].includes(normalized)) {
      return normalized as VideoParamsFormValues["codec"];
    }
  }
  return fallback;
};

const parseBitrateMode = (
  value: unknown,
  fallback: VideoParamsFormValues["advanced"]["h264"]["bitrateMode"]
): VideoParamsFormValues["advanced"]["h264"]["bitrateMode"] => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["cbr", "vbr"].includes(normalized)) {
      return normalized as VideoParamsFormValues["advanced"]["h264"]["bitrateMode"];
    }
  }
  return fallback;
};

const parseSpeedPreset = (
  value: unknown,
  fallback: VideoParamsFormValues["advanced"]["h264"]["speedPreset"]
): VideoParamsFormValues["advanced"]["h264"]["speedPreset"] => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (
      ["ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "slower", "veryslow"].includes(
        normalized
      )
    ) {
      return normalized as VideoParamsFormValues["advanced"]["h264"]["speedPreset"];
    }
  }
  return fallback;
};

const parseTune = (
  value: unknown,
  fallback: VideoParamsFormValues["advanced"]["h264"]["tune"]
): VideoParamsFormValues["advanced"]["h264"]["tune"] => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["none", "film", "animation", "grain", "zerolatency"].includes(normalized)) {
      return normalized as VideoParamsFormValues["advanced"]["h264"]["tune"];
    }
  }
  return fallback;
};

const parseBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  return fallback;
};

const sanitizeNumber = (value: unknown, fallback: number, options?: { allowNegativeOne?: boolean }): number => {
  let numericValue: number | null = null;

  if (typeof value === "number") {
    numericValue = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      const parsed = Number.parseFloat(trimmed);
      if (Number.isFinite(parsed)) {
        numericValue = parsed;
      }
    }
  }

  if (numericValue !== null && Number.isFinite(numericValue)) {
    if (options?.allowNegativeOne && numericValue === -1) {
      return -1;
    }
    return numericValue;
  }

  return fallback;
};

const mapStreamVideoSettingsToFormValues = (
  settings?: StreamVideoSettings | null,
  defaultBitrate: number = DEFAULT_VIDEO_BITRATE
): VideoParamsFormValues => {
  const defaults = createDefaultVideoParams({ bitrate: defaultBitrate });
  const stream = settings?.streams?.[0];
  if (!isRecord(stream)) {
    return defaults;
  }

  const codecSpecificSource = [stream["codecSpecificCfg"], stream["CodecSpecificCfg"]].find(isRecord);
  const advancedCandidate = [stream["advanced"], stream["Advanced"]].find(isRecord);
  const advancedSource = (codecSpecificSource ?? advancedCandidate ?? stream) as Record<string, unknown>;

  const frameRateSource = pickFirst(stream, [
    "frameRate",
    "framerate",
    "Framerate",
    "FrameRate",
    "fps",
    "Fps"
  ]);
  const codecSource = pickFirst(stream, ["codec", "Codec", "videoCodec", "VideoCodec"]);
  const bFramesSource = pickFirst(advancedSource, ["bFrames", "b_frames", "Bframes"]);
  const bitrateModeSource = pickFirst(advancedSource, ["bitrateMode", "mode", "BitrateMode"]);
  const referenceFrameSource = pickFirst(advancedSource, ["referenceFrame", "reference_frames", "Ref"]);
  const keyIntervalMaxSource = pickFirst(advancedSource, ["keyIntervalMax", "gop", "key_interval_max", "KeyIntMax"]);
  const speedPresetSource = pickFirst(advancedSource, ["speedPreset", "preset", "SpeedPreset"]);
  const tuneSource = pickFirst(advancedSource, ["tune", "Tune"]);
  const audSource = pickFirst(advancedSource, ["aud", "Aud"]);
  const cabacSource = pickFirst(advancedSource, ["cabac", "Cabac"]);
  const intraRefreshSource = pickFirst(advancedSource, ["intraRefresh", "intra_refresh", "IntraRefresh"]);

  const h264Defaults = defaults.advanced.h264;
  const vp8Defaults = defaults.advanced.vp8;
  const vp9Defaults = defaults.advanced.vp9;

  return {
    resolution: parseResolutionValue(stream, defaults.resolution),
    frameRate: sanitizeNumber(frameRateSource, typeof defaults.frameRate === 'number' ? defaults.frameRate : 30, {
      allowNegativeOne: true
    }),
    codec: parseCodec(codecSource, defaults.codec),
    bitrate: parseNumber(
      stream.bitrateKbps ??
        stream.bitrate_kbps ??
        stream.bitrate ??
        stream.Bitrate ??
        stream.BitrateKbps ??
        stream.videoBitrate,
      typeof defaults.bitrate === 'number' ? defaults.bitrate : DEFAULT_VIDEO_BITRATE
    ),
    advanced: {
      h264: {
        bFrames: parseNumber(bFramesSource, typeof h264Defaults.bFrames === 'number' ? h264Defaults.bFrames : 0),
        bitrateMode: parseBitrateMode(bitrateModeSource, h264Defaults.bitrateMode),
        referenceFrame: parseNumber(referenceFrameSource, typeof h264Defaults.referenceFrame === 'number' ? h264Defaults.referenceFrame : 3),
        keyIntervalMax: parseNumber(keyIntervalMaxSource, typeof h264Defaults.keyIntervalMax === 'number' ? h264Defaults.keyIntervalMax : 30),
        speedPreset: parseSpeedPreset(speedPresetSource, h264Defaults.speedPreset),
        tune: parseTune(tuneSource, h264Defaults.tune),
        aud: parseBoolean(audSource, h264Defaults.aud),
        cabac: parseBoolean(cabacSource, h264Defaults.cabac),
        intraRefresh: parseBoolean(intraRefreshSource, h264Defaults.intraRefresh)
      },
      vp8: { ...vp8Defaults },
      vp9: { ...vp9Defaults }
    }
  };
};

const parseOptionalNumber = (value: unknown): number | "" => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }
    const parsed = Number.parseFloat(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return "";
};

const parseOptionalString = (value: unknown): string => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return "";
};

const mapStreamSrtSettingsToFormValues = (settings?: StreamSrtSettings | null): SrtInputParamsFormValues => {
  const defaults = createDefaultSrtInputParams();
  if (!isRecord(settings)) {
    return defaults;
  }

  const latencySource = pickFirst(settings, ["latency", "Latency"]);
  const passphraseSource = pickFirst(settings, ["passphrase", "Passphrase"]);
  const pbkeylenSource = pickFirst(settings, ["pbkeylen", "Pbkeylen"]);
  const streamIdSource = pickFirst(settings, ["streamid", "Streamid"]);
  const transtypeSource = pickFirst(settings, ["transtype", "Transtype"]);
  const blockingSource = pickFirst(settings, ["blocking", "Blocking"]);

  const latency = parseOptionalNumber(latencySource);
  const passphrase = parseOptionalString(passphraseSource);
  const pbkeylen = parseOptionalNumber(pbkeylenSource);
  const streamid = parseOptionalString(streamIdSource);
  const transtype = parseOptionalString(transtypeSource);
  const normalizedTranstype = transtype ? transtype.toLowerCase() : "";
  const resolvedTranstype = normalizedTranstype === "file" || normalizedTranstype === "live"
    ? normalizedTranstype
    : defaults.transtype;

  return {
    latency: latency === "" ? defaults.latency : latency,
    passphrase: passphrase || defaults.passphrase,
    pbkeylen: pbkeylen === "" ? defaults.pbkeylen : pbkeylen,
    streamid: streamid || defaults.streamid,
    transtype: resolvedTranstype,
    blocking: parseBoolean(blockingSource, defaults.blocking)
  };
};

const mapStreamUdsSettingsToFormValues = (settings?: StreamUdsSettings | null): UdsInputParamsFormValues => {
  const defaults = createDefaultUdsInputParams();
  if (!isRecord(settings)) {
    return defaults;
  }

  const modeSource = pickFirst(settings, ["mode", "Mode"]);
  const normalizedMode = typeof modeSource === "string" ? modeSource.trim().toLowerCase() : "";
  const mode: UdsInputParamsFormValues["mode"] =
    normalizedMode === "datagram" ? "datagram" : defaults.mode;

  return {
    mode
  };
};

const mapStreamRtspSettingsToFormValues = (settings?: StreamRtspSettings | null): RtspInputParamsFormValues => {
  const defaults = createDefaultRtspInputParams();
  if (!isRecord(settings)) {
    return defaults;
  }

  const latencySource = pickFirst(settings, ["latency", "Latency"]);
  const userSource = pickFirst(settings, ["user", "User"]);
  const passwordSource = pickFirst(settings, ["password", "Password"]);
  const forceTcpSource = pickFirst(settings, ["forceTcp", "ForceTcp"]);

  const latency = parseOptionalNumber(latencySource);
  const user = parseOptionalString(userSource);
  const password = parseOptionalString(passwordSource);
  const forceTcp = typeof forceTcpSource === "boolean" ? forceTcpSource : defaults.forceTcp;

  return {
    latency: latency === "" ? defaults.latency : latency,
    user,
    password,
    forceTcp
  };
};

const mapStreamFileSettingsToFormValues = (settings?: StreamFileSettings | null): FileInputParamsFormValues => {
  const defaults = createDefaultFileInputParams();
  if (!isRecord(settings)) {
    return defaults;
  }

  const loopSource = pickFirst(settings, ["loop", "Loop"]);
  const mpegModeSource = pickFirst(settings, ["mpegTimestampsMode", "MpegTimestampsMode"]);
  const klvModeSource = pickFirst(settings, ["klvTimestampsMode", "KlvTimestampsMode"]);
  const klvStartTimeSource = pickFirst(settings, ["klvStartTime", "KlvStartTime"]);

  const loop = parseBoolean(loopSource, defaults.loop);

  const mpegModeRaw = parseOptionalString(mpegModeSource).toLowerCase();
  const mpegTimestampsMode =
    mpegModeRaw === "original" || mpegModeRaw === "restamp" || mpegModeRaw === "regenerate"
      ? mpegModeRaw
      : defaults.mpegTimestampsMode;

  const klvModeRaw = parseOptionalString(klvModeSource).toLowerCase();
  let klvTimestampsMode = defaults.klvTimestampsMode;
  if (klvModeRaw === "original" || klvModeRaw === "restamp") {
    klvTimestampsMode = klvModeRaw;
  } else if (klvModeRaw === "currenttime" || klvModeRaw === "current_time") {
    klvTimestampsMode = "currentTime";
  }

  const klvStartTime = parseOptionalString(klvStartTimeSource);

  return {
    loop,
    mpegTimestampsMode,
    klvTimestampsMode,
    klvStartTime: klvStartTime || null
  };
};

const defaultPcapValues = (): PcapFormValues => ({ file: "", hostMediaDrive: "", sourceIp: "", destinationIp: "", sourcePort: "", destinationPort: "", loop: false });
const mapStreamPcapSettingsToFormValues = (settings?: StreamPcapSettings | null): PcapFormValues => {
  const defaults = defaultPcapValues();
  if (!isRecord(settings)) return defaults;
  return {
    file: parseOptionalString(pickFirst(settings, ["file", "File"])),
    hostMediaDrive: parseOptionalString(pickFirst(settings, ["hostMediaDrive", "HostMediaDrive"])),
    sourceIp: parseOptionalString(pickFirst(settings, ["sourceIp", "SourceIP"])),
    destinationIp: parseOptionalString(pickFirst(settings, ["destinationIp", "DestinationIP"])),
    sourcePort: parseOptionalNumber(pickFirst(settings, ["sourcePort", "SourcePort"])),
    destinationPort: parseOptionalNumber(pickFirst(settings, ["destinationPort", "DestinationPort"])),
    loop: parseBoolean(pickFirst(settings, ["loop", "Loop"]), false)
  };
};

const normalizeTargetType = (value: string): StreamTargetForm["type"] => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "whip" || normalized === "llhls" || normalized === "srt" || normalized === "uds" || normalized === "rtsp") {
    return normalized;
  }
  if (normalized === "rawpcap") {
    return "rawPcap";
  }
  return "udp";
};

const mapStreamTargetsToFormValues = (targets?: StreamTarget[] | null): StreamTargetForm[] => {
  if (!Array.isArray(targets)) {
    return [];
  }
  return targets
    .map((target) => {
      if (!target || typeof target.id !== "string" || !target.id.trim()) {
        return null;
      }
      const engine = Array.isArray(target.engines) ? target.engines[0] : undefined;
      const engineType = typeof engine?.type === "string" ? engine.type.trim().toLowerCase() : "";
      const preserveOriginal = Boolean(engineType && !["udp", "srt", "uds", "rtsp", "rawpcap", "llhls", "whip"].includes(engineType));
      const config = isRecord(engine?.config) ? engine?.config : {};
      const nameCandidate = pickFirst(config as Record<string, unknown>, ["name", "Name"]);
      const descriptionCandidate = pickFirst(config as Record<string, unknown>, ["description", "Description"]);
      const urlCandidate = pickFirst(config as Record<string, unknown>, ["url", "URL"]);
      const destinationModeCandidate = pickFirst(
        config as Record<string, unknown>,
        ["destinationMode", "DestinationMode"]
      );
      const networkInterfaceCandidate = pickFirst(
        config as Record<string, unknown>,
        ["networkInterface", "NetworkInterface"]
      );
      const ttlCandidate = pickFirst(config as Record<string, unknown>, ["ttl", "TTL"]);
      const bufferSizeCandidate = pickFirst(config as Record<string, unknown>, ["bufferSize", "BufferSize"]);
      const latencyCandidate = pickFirst(config as Record<string, unknown>, ["latency", "Latency"]);
      const passphraseCandidate = pickFirst(config as Record<string, unknown>, ["passphrase", "Passphrase"]);
      const pbkeylenCandidate = pickFirst(config as Record<string, unknown>, ["pbkeylen", "Pbkeylen"]);
      const streamIdCandidate = pickFirst(config as Record<string, unknown>, ["streamid", "Streamid"]);
      const transtypeCandidate = pickFirst(config as Record<string, unknown>, ["transtype", "Transtype"]);
      const blockingCandidate = pickFirst(config as Record<string, unknown>, ["blocking", "Blocking"]);
      const srtDefaults = createDefaultSrtInputParams();
      const latency = parseOptionalNumber(latencyCandidate);
      const pbkeylen = parseOptionalNumber(pbkeylenCandidate);
      const rawModeCandidate = pickFirst(config as Record<string, unknown>, ["mode", "Mode"]);
      const udsMode = typeof rawModeCandidate === "string" && rawModeCandidate.trim().toLowerCase() === "datagram" ? "datagram" : "connection";
      const interfaceIdCandidate = pickFirst(config as Record<string, unknown>, ["interfaceId", "InterfaceID"]);
      const destinationIpCandidate = pickFirst(config as Record<string, unknown>, ["destinationIp", "DestinationIP"]);
      const destinationPortCandidate = pickFirst(config as Record<string, unknown>, ["destinationPort", "DestinationPort"]);
      const destinationMacCandidate = pickFirst(config as Record<string, unknown>, ["destinationMac", "DestinationMAC"]);
      const spoofCandidate = pickFirst(config as Record<string, unknown>, ["allowSpoofedSource", "AllowSpoofedSource"]);
      const queueSizeCandidate = pickFirst(config as Record<string, unknown>, ["queueSize", "QueueSize"]);
      const passphrase =
        typeof passphraseCandidate === "string" && passphraseCandidate.trim()
          ? passphraseCandidate.trim()
          : srtDefaults.passphrase;
      const streamid =
        typeof streamIdCandidate === "string" && streamIdCandidate.trim()
          ? streamIdCandidate.trim()
          : srtDefaults.streamid;
      const transtypeRaw =
        typeof transtypeCandidate === "string" && transtypeCandidate.trim()
          ? transtypeCandidate.trim().toLowerCase()
          : srtDefaults.transtype;
      const transtype =
        transtypeRaw === "live" || transtypeRaw === "file" ? transtypeRaw : srtDefaults.transtype;
      const blocking =
        typeof blockingCandidate === "boolean" ? blockingCandidate : srtDefaults.blocking;
      const ttl = parseOptionalNumber(ttlCandidate);
      const bufferSize = parseOptionalNumber(bufferSizeCandidate);
      return {
        payloadMode: config.payloadMode === "h264Sei" ? "h264Sei" : "fullStream",
        sei: normalizeSEI(config),
        id: target.id,
        inputType: target.inputType?.trim().toLowerCase() === "processor" ? "processor" : "source",
        inputId: target.inputType?.trim().toLowerCase() === "processor" ? (target.inputId ?? "").trim() : "",
        originalTarget: { ...target },
        originalEngine: engine ? { ...engine } : undefined,
        additionalEngines: Array.isArray(target.engines) ? target.engines.slice(1).map((item) => ({ ...item })) : [],
        preserveOriginal,
        rtsp: normalizeRTSP(config),
        llhls: normalizeLlHls(config),
        whip: normalizeWhip(config),
        name: typeof nameCandidate === "string" && nameCandidate.trim() ? nameCandidate.trim() : target.id,
        description: typeof descriptionCandidate === "string" ? descriptionCandidate.trim() : "",
        type: normalizeTargetType(typeof engine?.type === "string" ? engine.type : ""),
        destinationMode:
          typeof destinationModeCandidate === "string" &&
          destinationModeCandidate.trim().toLowerCase() === "captured"
            ? "captured"
            : "configured",
        url: typeof urlCandidate === "string" ? urlCandidate.trim() : "",
        networkInterface:
          typeof networkInterfaceCandidate === "string" ? networkInterfaceCandidate.trim() : "",
        ttl: ttl === "" ? 64 : ttl,
        bufferSize: bufferSize === "" ? 4096 : bufferSize,
        udsMode,
        srt: {
          latency: latency === "" ? srtDefaults.latency : latency,
          passphrase,
          pbkeylen: pbkeylen === "" ? srtDefaults.pbkeylen : pbkeylen,
          streamid,
          transtype,
          blocking
        },
        rawPcap: {
          mode: typeof rawModeCandidate === "string" && rawModeCandidate.toLowerCase() === "original" ? "original" : "rewrite",
          interfaceId: typeof interfaceIdCandidate === "string" ? interfaceIdCandidate.trim() : "",
          destinationIp: typeof destinationIpCandidate === "string" ? destinationIpCandidate.trim() : "",
          destinationPort: parseOptionalNumber(destinationPortCandidate),
          destinationMac: typeof destinationMacCandidate === "string" ? destinationMacCandidate.trim() : "",
          allowSpoofedSource: parseBoolean(spoofCandidate, false),
          queueSize: parseOptionalNumber(queueSizeCandidate) || 256
        }
      } satisfies StreamTargetForm;
    })
    .filter((target): target is Exclude<typeof target, null> => target !== null)
};

const buildStreamTargetsPayload = (targets: StreamTargetForm[]): StreamTarget[] => {
  return targets
    .map((target) => {
      if (target.preserveOriginal && target.originalTarget) {
        return target.originalTarget as StreamTarget;
      }
      const id = target.id.trim();
      if (!id) {
        return null;
      }
      const normalizedType = normalizeTargetType(target.type);
      const ttl =
        normalizedType === "udp" && typeof target.ttl === "number" && Number.isFinite(target.ttl)
          ? Math.round(target.ttl)
          : undefined;
      const bufferSize =
        (normalizedType === "udp" || normalizedType === "uds") &&
        typeof target.bufferSize === "number" &&
        Number.isFinite(target.bufferSize)
          ? Math.round(target.bufferSize)
          : undefined;
      const srtConfig =
        normalizedType === "srt"
          ? (() => {
              const latency =
                typeof target.srt.latency === "number" && Number.isFinite(target.srt.latency)
                  ? Math.max(0, Math.round(target.srt.latency))
                  : undefined;
              const passphrase = target.srt.passphrase.trim();
              const pbkeylen =
                typeof target.srt.pbkeylen === "number" && Number.isFinite(target.srt.pbkeylen)
                  ? Math.max(0, Math.round(target.srt.pbkeylen))
                  : undefined;
              const streamid = target.srt.streamid.trim();
              const transtype = target.srt.transtype.trim();
              return {
                ...(latency !== undefined ? { latency } : {}),
                ...(passphrase ? { passphrase } : {}),
                ...(passphrase && pbkeylen !== undefined ? { pbkeylen } : {}),
                ...(streamid ? { streamid } : {}),
                ...(transtype ? { transtype } : {}),
                blocking: Boolean(target.srt.blocking)
              };
            })()
          : undefined;
      const originalEngineConfig = isRecord(target.originalEngine?.config) ? target.originalEngine.config : {};
      return {
        ...(target.originalTarget ?? {}),
        id,
        inputType: target.type === "rawPcap" ? "source" : target.inputType,
        inputId: target.type === "rawPcap" || target.inputType === "source" ? "" : target.inputId,
        engines: [
          {
            ...(target.originalEngine ?? {}),
            type: normalizedType,
            config: {
              ...clearSEIConfig(clearRTSPConfig(originalEngineConfig)),
              ...seiPayload(target),
              name: target.name.trim(),
              description: target.description.trim(),
              ...(normalizedType === "rtsp" ? rtspPayload(target.rtsp) : normalizedType === "whip" ? whipPayload(target.whip, target.url) : normalizedType === "llhls" ? llHlsPayload(target.llhls) : normalizedType === "rawPcap"
                ? {
                    mode: target.rawPcap.mode,
                    interfaceId: target.rawPcap.interfaceId.trim(),
                    destinationIp: target.rawPcap.destinationIp.trim(),
                    destinationPort:
                      typeof target.rawPcap.destinationPort === "number"
                        ? Math.round(target.rawPcap.destinationPort)
                        : 0,
                    destinationMac: target.rawPcap.destinationMac.trim(),
                    allowSpoofedSource: Boolean(target.rawPcap.allowSpoofedSource),
                    queueSize:
                      typeof target.rawPcap.queueSize === "number"
                        ? Math.round(target.rawPcap.queueSize)
                        : 256
                  }
                : {
                    destinationMode: normalizedType === "udp" ? target.destinationMode : "configured",
                    ...(normalizedType !== "udp" || target.destinationMode !== "captured"
                      ? { url: target.url.trim() }
                      : {}),
                    ...(target.networkInterface.trim()
                      ? { networkInterface: target.networkInterface.trim() }
                      : {}),
                    ...(ttl ? { ttl } : {}),
                    ...(bufferSize ? { bufferSize } : {}),
                    ...(normalizedType === "uds" ? { mode: target.udsMode } : {}),
                    ...(srtConfig ? srtConfig : {})
                  })
            }
          },
          ...(target.additionalEngines ?? []) as StreamTargetEngine[]
        ]
      } satisfies StreamTarget;
    })
    .filter((target): target is Exclude<typeof target, null> => target !== null)
};

const createFlywheelProcessorId = (): string => {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `flywheel-${suffix}`;
};

const mapFlywheelToFormValues = (processors?: StreamProcessor[] | null): FlywheelFormValues => {
  const defaults = createDefaultFlywheelValues();
  for (const processor of processors ?? []) {
    for (const engine of processor.engines ?? []) {
      if (engine.type.trim().toLowerCase() !== "stview.flywheel") continue;
      const config = isRecord(engine.config) ? engine.config : {};
      const numberValue = (key: string, fallback: number) => parseNumber(config[key], fallback);
      const codec = String(config.videoCodec ?? defaults.videoCodec).toLowerCase();
      const rateControl = String(config.rateControl ?? "vbr").toLowerCase();
      const klvType = String(config.klvType ?? defaults.klvType).toLowerCase();
      const defaultTemplate = syntheticKlvNativeTemplate(defaults.syntheticKlvTemplateJson);
      const syntheticKlvTemplateJson = syntheticKlvTemplateDocument(
        String(config.syntheticKlvFieldsJson ?? defaultTemplate.syntheticKlvFieldsJson),
        numberValue("syntheticKlvRevision", defaultTemplate.syntheticKlvRevision)
      );
      return {
        enabled: engine.enabled !== false,
        ...flywheelCaptionValues(config),
        processorId: processor.id,
        engineId: engine.id ?? defaults.engineId,
        videoCodec: codec === "h265" ? "h265" : "h264",
        rateControl: rateControl === "vbr" ? "vbr" : "cbr",
        videoBitrateKbps: numberValue("videoBitrateKbps", defaults.videoBitrateKbps),
        videoMaxBitrateKbps: numberValue("videoMaxBitrateKbps", numberValue("videoBitrateKbps", defaults.videoBitrateKbps)),
        videoKeyframeInterval: numberValue("videoKeyframeInterval", defaults.videoKeyframeInterval),
        videoPid: numberValue("videoPid", defaults.videoPid),
        framerateFps: frameRateText(numberValue("fpsNumerator",numberValue("framerateFps",30)),numberValue("fpsDenominator",1)),
 preserveDetectedKlvType:parseBoolean(config.preserveDetectedKlvType,false),
        resolutionWidth: numberValue("resolutionWidth", defaults.resolutionWidth),
        resolutionHeight: numberValue("resolutionHeight", defaults.resolutionHeight),
        preferHardwareAcceleration: parseBoolean(config.preferHardwareAcceleration, true),
        syntheticKlvEnabled: parseBoolean(config.syntheticKlvEnabled, true),
        syntheticKlvDefaultPid: numberValue("syntheticKlvDefaultPid", defaults.syntheticKlvDefaultPid),
        klvType: klvType === "sync" ? "sync" : "async",
        syntheticKlvRate: numberValue("syntheticKlvRate", defaults.syntheticKlvRate),
        syntheticKlvTemplateJson
      };
    }
  }
  return defaults;
};

const buildProcessorsPayload = (processors: StreamProcessor[], flywheel: FlywheelFormValues): StreamProcessor[] => {
  const result = (processors ?? []).map((processor) => ({
    ...processor,
    engines: (processor.engines ?? []).map((engine) => ({ ...engine, config: isRecord(engine.config) ? { ...engine.config } : engine.config }))
  }));
  let processorIndex = -1;
  let engineIndex = -1;
  result.some((processor, pIndex) => (processor.engines ?? []).some((engine, eIndex) => {
    if (engine.type.trim().toLowerCase() !== "stview.flywheel") return false;
    processorIndex = pIndex;
    engineIndex = eIndex;
    return true;
  }));
  if (processorIndex < 0 && !flywheel.enabled) return result;
  const syntheticKlvTemplate = syntheticKlvNativeTemplate(flywheel.syntheticKlvTemplateJson);
  const config = {
    videoCodec: flywheel.videoCodec,
    ...flywheelCaptionValues(flywheel),
    rateControl: flywheel.rateControl,
    videoBitrateKbps: Math.round(flywheel.videoBitrateKbps),
    videoMaxBitrateKbps: Math.round(flywheel.videoMaxBitrateKbps),
    videoKeyframeInterval: Math.round(flywheel.videoKeyframeInterval),
    videoPid: Math.round(flywheel.videoPid),
    framerateFps: Math.round(Number(flywheel.framerateFps)),
 fpsNumerator:frameRateFraction(flywheel.framerateFps)?.numerator,
 fpsDenominator:frameRateFraction(flywheel.framerateFps)?.denominator,
 preserveDetectedKlvType:flywheel.preserveDetectedKlvType,
    resolutionWidth: Math.round(flywheel.resolutionWidth),
    resolutionHeight: Math.round(flywheel.resolutionHeight),
    preferHardwareAcceleration: flywheel.preferHardwareAcceleration,
    syntheticKlvEnabled: flywheel.syntheticKlvEnabled,
    syntheticKlvDefaultPid: Math.round(flywheel.syntheticKlvDefaultPid),
    klvType: flywheel.klvType,
    syntheticKlvRate: Math.round(flywheel.syntheticKlvRate),
    ...syntheticKlvTemplate
  };
  if (processorIndex < 0) {
    return [...result, { id: flywheel.processorId || createFlywheelProcessorId(), engines: [{ id: flywheel.engineId, type: "stview.flywheel", enabled: true, config }] }];
  }
  const processor = result[processorIndex];
  const engines = processor.engines ?? [];
  const original = engines[engineIndex];
  engines[engineIndex] = { ...original, id: flywheel.engineId || original.id, type: "stview.flywheel", enabled: flywheel.enabled, config: { ...(isRecord(original.config) ? original.config : {}), ...config } };
  result[processorIndex] = { ...processor, id: flywheel.processorId || processor.id, engines };
  return result;
};
type SrtSettingsPayload = {
  Latency?: number;
  Passphrase?: string;
  Pbkeylen?: number;
  Streamid?: string;
  Transtype?: string;
  Blocking?: boolean;
};

const buildSrtSettingsPayload = (values: SrtInputParamsFormValues): SrtSettingsPayload | undefined => {
  const payload: SrtSettingsPayload = {};
  if (typeof values.latency === "number" && Number.isFinite(values.latency)) {
    payload.Latency = Math.round(values.latency);
  }
  const passphrase = values.passphrase.trim();
  if (passphrase) {
    payload.Passphrase = passphrase;
    if (typeof values.pbkeylen === "number" && Number.isFinite(values.pbkeylen)) {
      payload.Pbkeylen = Math.round(values.pbkeylen);
    }
  }
  const streamId = values.streamid.trim();
  if (streamId) {
    payload.Streamid = streamId;
  }
  const transtype = values.transtype.trim();
  if (transtype) {
    payload.Transtype = transtype;
  }
  if (typeof values.blocking === "boolean") {
    payload.Blocking = values.blocking;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};

type UdsSettingsPayload = {
  Mode?: string;
};

const buildUdsSettingsPayload = (values: UdsInputParamsFormValues): UdsSettingsPayload | undefined => {
  const normalizedMode = values.mode?.trim().toLowerCase();
  const payload: UdsSettingsPayload = {};
  if (normalizedMode === "datagram" || normalizedMode === "connection") {
    payload.Mode = normalizedMode;
  }
  return Object.keys(payload).length > 0 ? payload : undefined;
};

type RtspSettingsPayload = {
  User?: string;
  Password?: string;
  Latency?: number;
  ForceTcp?: boolean;
};

const buildRtspSettingsPayload = (values: RtspInputParamsFormValues): RtspSettingsPayload | undefined => {
  const payload: RtspSettingsPayload = {};
  const user = values.user.trim();
  const password = values.password.trim();
  if (user) {
    payload.User = user;
  }
  if (password) {
    payload.Password = password;
  }
  if (typeof values.latency === "number" && Number.isFinite(values.latency)) {
    payload.Latency = Math.max(0, Math.round(values.latency));
  }
  if (typeof values.forceTcp === "boolean") {
    payload.ForceTcp = values.forceTcp;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};

type FileSettingsPayload = {
  Loop?: boolean;
  MpegTimestampsMode?: string;
  KlvTimestampsMode?: string;
  KlvStartTime?: string;
};

const buildFileSettingsPayload = (values: FileInputParamsFormValues): FileSettingsPayload | undefined => {
  const payload: FileSettingsPayload = {};
  payload.Loop = Boolean(values.loop);

  const mpegMode = values.mpegTimestampsMode?.trim();
  if (mpegMode) {
    payload.MpegTimestampsMode = mpegMode;
  }
  const klvMode = values.klvTimestampsMode?.trim();
  if (klvMode) {
    payload.KlvTimestampsMode = klvMode;
  }
  if (values.klvTimestampsMode === "restamp") {
    const klvStartTime = (values.klvStartTime ?? "").trim();
    if (klvStartTime) {
      payload.KlvStartTime = klvStartTime;
    }
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};

const buildPcapSettingsPayload = (values: PcapFormValues) => ({
  file: values.file.trim(), hostMediaDrive: values.hostMediaDrive.trim(), sourceIp: values.sourceIp.trim(), destinationIp: values.destinationIp.trim(),
  sourcePort: typeof values.sourcePort === "number" ? values.sourcePort : 0,
  destinationPort: typeof values.destinationPort === "number" ? values.destinationPort : 0,
  loop: values.loop
});

const parseResolutionParts = (value: string): { width?: number; height?: number } => {
  const match = value.trim().match(/^(\d+)x(\d+)$/i);
  if (!match) {
    return {};
  }
  const width = Number.parseInt(match[1], 10);
  const height = Number.parseInt(match[2], 10);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return {};
  }
  return { width, height };
};

const buildVideoStreamsPayload = (
  video: VideoParamsFormValues,
  defaultBitrate: number = DEFAULT_VIDEO_BITRATE
): VideoStreamConfig[] => {
  const defaults = createDefaultVideoParams({ bitrate: defaultBitrate });
  const { width, height } = parseResolutionParts(video.resolution);
  const sanitizedFrameRate = sanitizeNumber(video.frameRate, typeof defaults.frameRate === 'number' ? defaults.frameRate : 30, { allowNegativeOne: true });
  const sanitizedBitrate = sanitizeNumber(video.bitrate, typeof defaults.bitrate === 'number' ? defaults.bitrate : DEFAULT_VIDEO_BITRATE);
  const base: VideoStreamConfig = {
    codec: video.codec,
    frameRate: sanitizedFrameRate,
    framerate: sanitizedFrameRate,
    Framerate: sanitizedFrameRate
  };
  base.bitrate = sanitizedBitrate;
  base.bitrateKbps = sanitizedBitrate;

  if (width && height) {
    base.width = width;
    base.height = height;
    base.resolution = { width, height };
  } else if (video.resolution === NO_SCALING_OPTION) {
    base.width = -1;
    base.height = -1;
    base.resolution = { width: -1, height: -1 };
  }

  if (video.codec === "h264") {
    const h264Defaults = defaults.advanced.h264;
    const h264Advanced = video.advanced.h264;

    const sanitizedBFrames = sanitizeNumber(h264Advanced.bFrames, typeof h264Defaults.bFrames === 'number' ? h264Defaults.bFrames : 0);
    const sanitizedReferenceFrame = sanitizeNumber(h264Advanced.referenceFrame, typeof h264Defaults.referenceFrame === 'number' ? h264Defaults.referenceFrame : 3);
    const sanitizedKeyIntervalMax = sanitizeNumber(h264Advanced.keyIntervalMax, typeof h264Defaults.keyIntervalMax === 'number' ? h264Defaults.keyIntervalMax : 30);
    const sanitizedBitrateMode = h264Advanced.bitrateMode;
    const sanitizedSpeedPreset = h264Advanced.speedPreset;
    const sanitizedTune = h264Advanced.tune;
    const sanitizedAud = Boolean(h264Advanced.aud);
    const sanitizedCabac = Boolean(h264Advanced.cabac);
    const sanitizedIntraRefresh = Boolean(h264Advanced.intraRefresh);

    const advanced: Record<string, unknown> = {
      bFrames: sanitizedBFrames,
      bitrateMode: sanitizedBitrateMode,
      referenceFrame: sanitizedReferenceFrame,
      keyIntervalMax: sanitizedKeyIntervalMax,
      speedPreset: sanitizedSpeedPreset,
      tune: sanitizedTune,
      aud: sanitizedAud,
      cabac: sanitizedCabac,
      intraRefresh: sanitizedIntraRefresh
    };

    Object.keys(advanced).forEach((key) => {
      if (advanced[key] === undefined) {
        delete advanced[key];
      }
    });

    if (Object.keys(advanced).length > 0) {
      base.advanced = advanced;
    }

    const codecSpecificCfg: Record<string, unknown> = {
      SpeedPreset: sanitizedSpeedPreset,
      Tune: sanitizedTune,
      Aud: sanitizedAud,
      Cabac: sanitizedCabac,
      IntraRefresh: sanitizedIntraRefresh,
      BitrateMode: sanitizedBitrateMode
    };

    if (Number.isFinite(sanitizedBFrames)) {
      codecSpecificCfg.Bframes = Math.max(0, Math.round(sanitizedBFrames));
    }
    if (Number.isFinite(sanitizedReferenceFrame)) {
      codecSpecificCfg.Ref = Math.max(0, Math.round(sanitizedReferenceFrame));
    }
    if (Number.isFinite(sanitizedKeyIntervalMax)) {
      codecSpecificCfg.KeyIntMax = Math.max(0, Math.round(sanitizedKeyIntervalMax));
    }

    Object.keys(codecSpecificCfg).forEach((key) => {
      if (codecSpecificCfg[key] === undefined) {
        delete codecSpecificCfg[key];
      }
    });

    if (Object.keys(codecSpecificCfg).length > 0) {
      base.codecSpecificCfg = codecSpecificCfg;
    }
  }

  return [base];
};

const advancedH264ValidationSchema = Yup.object({
  bFrames: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("B-Frames must be a number")
    .min(0, "B-Frames cannot be negative")
    .max(16, "B-Frames must be 16 or fewer")
    .required("B-Frames is required"),
  bitrateMode: Yup.string()
    .transform((value) => value?.trim().toLowerCase() ?? "")
    .oneOf(["cbr", "vbr"], "Bitrate mode must be CBR or VBR")
    .required("Bitrate mode is required"),
  referenceFrame: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Reference frame must be a number")
    .min(0, "Reference frame cannot be negative")
    .max(16, "Reference frame must be 16 or fewer")
    .required("Reference frame is required"),
  keyIntervalMax: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Key interval max must be a number")
    .min(0, "Key interval max cannot be negative")
    .max(360, "Key interval max must be 360 or fewer")
    .required("Key interval max is required"),
  speedPreset: Yup.string()
    .transform((value) => value?.trim().toLowerCase() ?? "")
    .oneOf(
      ["ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "slower", "veryslow"],
      "Select a valid encoder preset"
    )
    .required("Speed preset is required"),
  tune: Yup.string()
    .transform((value) => value?.trim().toLowerCase() ?? "")
    .oneOf(["none", "film", "animation", "grain", "zerolatency"], "Select a valid tune option")
    .required("Tune is required"),
  aud: Yup.boolean(),
  cabac: Yup.boolean(),
  intraRefresh: Yup.boolean()
});

const emptyAdvancedCodecSchema = Yup.object({
  h264: Yup.object().shape({}),
  vp8: Yup.object().shape({}),
  vp9: Yup.object().shape({})
});

const videoValidationSchema = Yup.object({
  resolution: Yup.string()
    .transform((value) => value?.trim() ?? "")
    .required("Resolution is required"),
  frameRate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Frame rate must be a number")
    .min(-1, "Frame rate cannot be lower than -1")
    .max(120, "Frame rate must be 120 or fewer")
    .required("Frame rate is required"),
  codec: Yup.string()
    .transform((value) => value?.trim().toLowerCase() ?? "")
    .oneOf(
      VIDEO_CODEC_OPTIONS.map((option) => option.value),
      "Select a valid codec"
    )
    .required("Codec is required"),
  bitrate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Bitrate must be a number")
    .min(0, "Bitrate cannot be negative")
    .max(50000, "Bitrate must be 50,000 or fewer")
    .required("Bitrate is required"),
  advanced: Yup.object({
    h264: advancedH264ValidationSchema,
    vp8: emptyAdvancedCodecSchema,
    vp9: emptyAdvancedCodecSchema
  })
});

const srtValidationSchema = Yup.object({
  latency: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError("Latency must be a number")
    .test("min", "Latency must be at least 80 ms", (value) => value == null || value >= 80)
    .test("max", "Latency must be at most 8000 ms", (value) => value == null || value <= 8000),
  passphrase: Yup.string()
    .nullable()
    .transform((value) => value?.trim() ?? "")
    .test("passphrase-length", "Passphrase must be between 10 and 78 characters", (value) => {
      if (!value) {
        return true;
      }
      return value.length >= 10 && value.length < 79;
    }),
  pbkeylen: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError("Pbkeylen must be a number")
    .when("passphrase", {
      is: (passphrase: string | null | undefined) => Boolean(passphrase && passphrase.trim()),
      then: (schema) => schema.required("Pbkeylen is required when Passphrase is set"),
      otherwise: (schema) => schema.nullable()
    }),
  streamid: Yup.string()
    .transform((value) => value?.trim() ?? "")
    .max(512, "Stream ID must be at most 512 characters")
    .test("no-null-char", "Stream ID cannot contain null characters", (value) =>
      value ? !/\u0000/.test(value) : true
    ),
  transtype: Yup.string()
    .transform((value) => value?.trim().toLowerCase() ?? "")
    .oneOf(["live", "file"], "Transport type is required")
    .required("Transport type is required"),
  blocking: Yup.boolean().required("Blocking is required")
});

const createDefaultFormValues = (defaultBitrate: number = DEFAULT_VIDEO_BITRATE): StreamFormValues => ({
  sensor: "",
  platformId: "",
  type: STREAM_TYPE_OPTIONS[0].value,
  url: "",
  networkInterface: "",
  description: "",
  payloadType: DEFAULT_PAYLOAD_TYPE,
  active: true,
  autoStart: false,
  passthrough: true, // Default passthrough to true
  video: createDefaultVideoParams({ bitrate: defaultBitrate }),
  srt: createDefaultSrtInputParams(),
  uds: createDefaultUdsInputParams(),
  rtsp: createDefaultRtspInputParams(),
  file: createDefaultFileInputParams(),
  pcap: defaultPcapValues(),
  timeout: DEFAULT_TIMEOUT,
  offlineAfter: DEFAULT_OFFLINE_AFTER,
  bufferSizeKb: DEFAULT_BUFFER_SIZE_KB,
  lowLatency: false,
  cacheMs: DEFAULT_CACHE_MS,
  addPlatformMetadata: false,
  targets: [],
  processors: [],
  flywheel: createDefaultFlywheelValues()
});

const StreamFormDialog = ({
  open,
  onClose,
  editingStream,
  streams,
  platformOptions,
  isPlatformsLoading,
  isPlatformsError,
  platformsError,
  networkInterfaceOptions,
  isNetworkInterfacesLoading,
  loadNetworkInterfaces,
  authorizedFetch,
  resolveApiPath,
  accessToken,
  invalidateStreams,
  onShowSnackbar,
  isEditingStreamRunning,
  readOnly = false
}: StreamFormDialogProps) => {
  const configReadOnly = readOnly || isEditingStreamRunning;
  const configReadOnlyRef = useRef(configReadOnly);
  configReadOnlyRef.current = configReadOnly;
  const isDesktopShell = useMemo(() => isDesktopShellEnvironment(), []);
  const { serverConfig, refreshServerConfig } = useAppContext();
  const defaultVideoBitrate = isDesktopShell ? DESKTOP_DEFAULT_VIDEO_BITRATE : DEFAULT_VIDEO_BITRATE;
  const [hasRecastOption, setHasRecastOption] = useState(false);
  const recastAvailable = hasRecastOption || serverConfig.recastLicensed;
  const [streamMode, setStreamMode] = useState<StreamMode>("play");
  const [modeDirty, setModeDirty] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StreamFormTab>("general");
  const [hostMediaDialogOpen, setHostMediaDialogOpen] = useState(false);
  const [pcapFlows, setPcapFlows] = useState<PcapUDPFlow[]>([]);
  const [isPcapInspecting, setIsPcapInspecting] = useState(false);
  const [pcapInspectError, setPcapInspectError] = useState<string | null>(null);
  const pcapInspectRequestRef = useRef(0);
  const [captureUploadError, setCaptureUploadError] = useState<string | null>(null);
  const [isCaptureUploading, setIsCaptureUploading] = useState(false);
  const [captureUploadProgress, setCaptureUploadProgress] = useState(0);
  const captureUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [mediaUploadError, setMediaUploadError] = useState<string | null>(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0);
  const mediaUploadInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenConfigStreamHelp = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    void openExternalPath("/help/user-guide/config-stream.html");
  }, []);
  const initialMode = useMemo(() => normalizeStreamMode(editingStream?.mode), [editingStream]);

  useEffect(() => {
    if (open) {
      void refreshServerConfig();
      setDialogError(null);
      setActiveTab("general");
      setPcapFlows([]);
      setPcapInspectError(null);
      setIsPcapInspecting(false);
      pcapInspectRequestRef.current += 1;
    }
  }, [open, editingStream?.id, refreshServerConfig]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setStreamMode(initialMode);
    setModeDirty(false);
  }, [open, editingStream, initialMode]);

  useEffect(() => {
    let cancelled = false;
    const loadLicense = async () => {
      try {
        const response = await authorizedFetch("/api/config/license");
        const payload = (await response.json().catch(() => null)) as
          | { token?: string; isDemoMode?: boolean; error?: string }
          | null;
        if (!response.ok) {
          throw new Error(payload?.error ?? "Failed to load license info");
        }
        const options = decodeLicenseOptions(payload?.token ?? "");
        const hasRecast = options.some((option) => option.toLowerCase() === "recast") || payload?.isDemoMode === true;
        if (!cancelled) {
          setHasRecastOption(hasRecast);
        }
      } catch (error) {
        console.warn("Unable to resolve Recast license option", error);
        if (!cancelled) {
          setHasRecastOption(false);
        }
      }
    };

    if (open) {
      void loadLicense();
    }

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, open]);

  const resolveEffectiveMode = () => {
    if (recastAvailable) {
      return streamMode;
    }
    if (editingStream) {
      return normalizeStreamMode(editingStream.mode);
    }
    return "play";
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        sensor: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .required("Sensor is required")
          .test("no-spaces", "Sensor cannot contain spaces", function () {
            return typeof this.originalValue !== "string" || !/\s/.test(this.originalValue);
          })
          .test("unique-sensor", "A stream with this sensor already exists for the selected platform", function (value) {
            if (!value) {
              return true;
            }
            const normalizedSensor = value.trim().toLowerCase();
            const platformValue = getPlatformKey(this.parent.platformId);
            const currentId = editingStream?.id ?? null;
            return !streams.some((stream) => {
              const streamPlatform = getPlatformKey(stream.platformId, stream.platformName);
              return (
                stream.id !== currentId &&
                stream.sensor.trim().toLowerCase() === normalizedSensor &&
                streamPlatform === platformValue
              );
            });
          }),
        platformId: Yup.string().transform((value) => value?.trim() ?? ""),
        type: Yup.string()
          .transform((value) => value?.trim().toLowerCase() ?? "")
          .oneOf(
            STREAM_TYPE_OPTIONS.map((option) => option.value),
            "Type must be UDP, SRT, RTP, RTSP, UDS, FILE, or PCAP"
          )
          .required("Type is required"),
        payloadType: Yup.string()
          .transform((value) => value?.trim().toLowerCase() ?? "")
          .oneOf(
            PAYLOAD_TYPE_OPTIONS.map((option) => option.value),
            "Select a valid payload type"
          )
          .required("Payload type is required")
          .test("data-requires-passthrough", "Payload type \"Data\" requires stream Passthrough", function (value) {
            const isData = (value ?? "").trim().toLowerCase() === "data";
            const passthrough = Boolean(this.parent?.passthrough);
            if (isData && !passthrough) {
              return this.createError({ message: "Payload type \"Data\" requires stream Passthrough" });
            }
            return true;
          }),
        url: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .test("pattern",function(value){if(this.parent.type!=="pattern")return true;const message=validatePattern(value ?? "");return message?this.createError({message}):true;})
          .test("url-required", "URL is required", function (value) {
            const type = (this.parent.type ?? "").toLowerCase();
            return type === "pcap" || Boolean(value);
          })
          .test("matches-type-protocol", function (value) {
            const type = (this.parent.type ?? "").toLowerCase();
            if (!value || !type || !(type in TYPE_URL_PREFIX)) {
              return true;
            }
            const expectedPrefix = TYPE_URL_PREFIX[type as keyof typeof TYPE_URL_PREFIX];
            if (type === "uds") {
              if (
                value.toLowerCase().startsWith(expectedPrefix) ||
                value.trim().startsWith("/") ||
                value.trim().startsWith("\\\\")
              ) {
                return true;
              }
              return this.createError({ message: "URL must start with UDS or be a socket path/name" });
            }
            return value.toLowerCase().startsWith(expectedPrefix)
              ? true
              : this.createError({ message: `URL must start with ${expectedPrefix.toUpperCase()}` });
          })
          .test("single-protocol", function (value) {
            const type = (this.parent.type ?? "").toLowerCase();
            const expectedProtocol = getTypeProtocolPrefix(type);
            if (!value || !expectedProtocol) {
              return true;
            }

            const remainder = stripTypeProtocolFromUrl(value, expectedProtocol);
              return remainder.includes("://")
                ? this.createError({ message: "URL cannot contain multiple protocols" })
                : true;
          })
          .test("hostless-port", function (value) {
            if (!value) {
              return true;
            }
            const type = (this.parent.type ?? "").toLowerCase();
            const status = validateHostlessStreamSourceUrl(value, type);
            if (status === "invalid-port") {
              return this.createError({ message: "Port must be between 1 and 65535" });
            }
            if (status === "bare-port") {
              return this.createError({ message: "Enter port-only inputs as :port (for example, :5000)" });
            }
            return true;
          })
          .test("valid-host", "Enter a valid host or socket path", function (value) {
            if (!value) {
              return true;
            }
            const type = (this.parent.type ?? "").toLowerCase();
            if (validateHostlessStreamSourceUrl(value, type) === "valid") {
              return true;
            }
            try {
              const parsed = new URL(value);
              if (type === "uds") {
                return isValidUdsSocketPathValue(value);
              }
              if (type === "file") {
                return true;
              }
              if (type === "pcap" || type === "pattern") return true;
              return isValidHost(parsed.hostname);
            } catch {
              return false;
            }
          })
          .test("valid-url", "Enter a valid URL", function (value) {
            if (!value) {
              return true;
            }
            const type = (this.parent.type ?? "").toLowerCase();
            if (type === "uds") {
              return isValidUdsSocketPathValue(value);
            }
            if (validateHostlessStreamSourceUrl(value, type) === "valid") {
              return true;
            }
            try {
              // The WHATWG URL parser accepts custom protocols (udp, rtsp, etc.).
              // eslint-disable-next-line no-new
              new URL(value);
              return true;
            } catch {
              return false;
            }
          }),
        networkInterface: Yup.string().transform((value) => value?.trim() ?? ""),
        description: Yup.string().transform((value) => value?.trim()),
        timeout: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? undefined : value))
          .typeError("Timeout must be a number")
          .min(0, "Timeout must be between 0 and 3,600,000 ms")
          .max(3600000, "Timeout must be between 0 and 3,600,000 ms")
          .required("Timeout is required"),
        offlineAfter: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? undefined : value))
          .typeError("Offline after must be a number")
          .min(0, "Offline after must be between 0 and 3,600,000 ms")
          .max(3600000, "Offline after must be between 0 and 3,600,000 ms")
          .required("Offline after is required"),
        bufferSizeKb: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? undefined : value))
          .typeError("Buffer size must be a number of KB")
          .min(DEFAULT_BUFFER_SIZE_KB, "Buffer size must be at least 4,096 KB")
          .max(MAX_BUFFER_SIZE_KB, "Buffer size must not exceed 84,375 KB")
          .required("Buffer size is required"),
        lowLatency: Yup.boolean().required(),
        cacheMs: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? undefined : value))
          .typeError("Cache must be a number of milliseconds")
          .min(MIN_CACHE_MS, `Cache must be between ${MIN_CACHE_MS} and ${MAX_CACHE_MS} ms`)
          .max(MAX_CACHE_MS, `Cache must be between ${MIN_CACHE_MS} and ${MAX_CACHE_MS} ms`)
          .required("Cache is required"),
        addPlatformMetadata: Yup.boolean().test(
          "requires-platform",
          "Add Platform Metadata requires a platform",
          function (value) {
            const platformId = (this.parent?.platformId ?? "").trim();
            if (!platformId) {
              return !value;
            }
            return true;
          }
        ),
        srt: Yup.lazy((_value, { parent }) => {
          const typeValue = typeof parent?.type === "string" ? parent.type.trim().toLowerCase() : "";
          return typeValue === "srt" ? srtValidationSchema : Yup.object().shape({});
        }),
        uds: Yup.lazy((_value, { parent }) => {
          const typeValue = typeof parent?.type === "string" ? parent.type.trim().toLowerCase() : "";
          return typeValue === "uds"
            ? Yup.object({
                mode: Yup.string()
                  .transform((value) => value?.trim().toLowerCase() ?? "")
                  .oneOf(["connection", "datagram"], "Select a valid mode")
                  .test(
                    "datagram-available",
                    "Datagram mode is not supported on Windows",
                    (value) => value !== "datagram" || serverConfig.udsDatagramAvailable
                  )
                  .required("Mode is required")
              })
            : Yup.object().shape({});
        }),
        rtsp: Yup.lazy((_value, { parent }) => {
          const typeValue = typeof parent?.type === "string" ? parent.type.trim().toLowerCase() : "";
          return typeValue === "rtsp"
            ? Yup.object({
                user: Yup.string()
                  .transform((value) => value?.trim() ?? "")
                  .test("no-spaces", "User cannot contain spaces", (value) => !value || !/\s/.test(value))
                  .test("requires-password", "User is required when password is set", function (value) {
                    const password = (this.parent?.password ?? "").trim();
                    if (password && !value) {
                      return this.createError({ message: "User is required when password is set" });
                    }
                    return true;
                  }),
                password: Yup.string()
                  .transform((value) => value?.trim() ?? "")
                  .test("no-spaces", "Password cannot contain spaces", (value) => !value || !/\s/.test(value))
                  .test("requires-user", "Password is required when user is set", function (value) {
                    const user = (this.parent?.user ?? "").trim();
                    if (user && !value) {
                      return this.createError({ message: "Password is required when user is set" });
                    }
                    return true;
                  }),
                forceTcp: Yup.boolean().required("Force TCP is required"),
                latency: Yup.number()
                  .transform((value, originalValue) => (originalValue === "" ? null : value))
                  .nullable()
                  .typeError("Latency must be a number")
                  .test("min", "Latency must be at least 0 ms", (value) => value == null || value >= 0)
                  .test("max", "Latency must be at most 8,000 ms", (value) => value == null || value <= 8000)
              })
            : Yup.object().shape({});
        }),
        file: Yup.lazy((_value, { parent }) => {
          const typeValue = typeof parent?.type === "string" ? parent.type.trim().toLowerCase() : "";
          return typeValue === "file"
            ? Yup.object({
                loop: Yup.boolean().required("Loop is required"),
                mpegTimestampsMode: Yup.string()
                  .transform((value) => value?.trim().toLowerCase() ?? "")
                  .oneOf(["original", "restamp", "regenerate"], "Select a valid MPEG timestamps mode")
                  .required("MPEG timestamps mode is required"),
                klvTimestampsMode: Yup.string()
                  .transform((value) => value?.trim() ?? "")
                  .test(
                    "valid-klv-mode",
                    "Select a valid KLV timestamps mode",
                    (value) => {
                      const normalized = (value ?? "").trim().toLowerCase();
                      return normalized === "original" || normalized === "restamp" || normalized === "currenttime";
                    }
                  )
                  .required("KLV timestamps mode is required"),
                klvStartTime: Yup.string()
                  .nullable()
                  .transform((value) => {
                    if (typeof value !== "string") {
                      return value;
                    }
                    const trimmed = value.trim();
                    return trimmed || null;
                  })
                  .when("klvTimestampsMode", {
                    is: (mode: unknown) => (typeof mode === "string" ? mode.trim().toLowerCase() : "") === "restamp",
                    then: (schema) =>
                      schema.required("KLV Start Time is required when KLV timestamps mode is Restamp"),
                    otherwise: (schema) => schema.notRequired()
                  })
              })
            : Yup.object().shape({});
        }),
        pcap: Yup.lazy((_value, { parent }) => {
          const typeValue = typeof parent?.type === "string" ? parent.type.trim().toLowerCase() : "";
          return typeValue === "pcap" ? Yup.object({
            file: Yup.string().trim().required("Capture file is required").matches(/\.(pcap|pcapng)$/i, "Select a PCAP or PCAPNG file"),
            sourceIp: Yup.string().trim(), destinationIp: Yup.string().trim().required("Destination IP is required"),
            sourcePort: Yup.number().transform((value, original) => original === "" ? 0 : value).min(0).max(65535),
            destinationPort: Yup.number().typeError("Destination port is required").min(1).max(65535).required(), loop: Yup.boolean()
          }) : Yup.object().shape({});
        }),
        flywheel: Yup.object({
          enabled: Yup.boolean().required(),
          processorId: Yup.string().when("enabled", { is: true, then: (schema) => schema.required("Flywheel processor ID is required") }),
          videoCodec: Yup.string().oneOf(["h264", "h265"]).required(),
          rateControl: Yup.string().oneOf(["cbr", "vbr"]).required(),
          videoBitrateKbps: Yup.number().integer().min(1).max(100000).required(),
          videoMaxBitrateKbps: Yup.number().integer().min(1).max(100000).required(),
          videoKeyframeInterval: Yup.number().integer().min(1).max(600).required(),
          videoPid: Yup.number().integer().min(32).max(8186).required(),
          framerateFps: Yup.string().required().test("fps","Framerate must be 1-60 fps",v=>!frameRateError("Framerate",v ?? "",{max:60})),
          resolutionWidth: Yup.number().integer().min(320).max(8192).required(),
          resolutionHeight: Yup.number().integer().min(240).max(8192).required(),
          syntheticKlvDefaultPid: Yup.number().integer().min(32).max(8186).required(),
          klvType: Yup.string().oneOf(["async", "sync"]).required(),
          syntheticKlvEnabled:Yup.boolean().required(),
          syntheticKlvRate: Yup.number().integer().min(1).max(60).required(),
          syntheticKlvTemplateJson: Yup.string().required()
        })
          .test("distinct-pids", "Video and fallback KLV PIDs must differ", (value) => !value?.syntheticKlvEnabled || value.videoPid !== value.syntheticKlvDefaultPid)
          .test("vbr-maximum", "Maximum bitrate must be at least the target bitrate", function (value) {
            if (!value || value.rateControl !== "vbr" || value.videoMaxBitrateKbps >= value.videoBitrateKbps) return true;
            return this.createError({ path: `${this.path}.videoMaxBitrateKbps`, message: "Maximum bitrate must be at least the target bitrate" });
          }),
        video: Yup.lazy((_value, { parent }) =>
          parent?.type === "pattern" || parent?.passthrough ? Yup.object().shape({}) : videoValidationSchema
        )
      }),
    [editingStream?.id, serverConfig.udsDatagramAvailable, streams]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.65)" } }, paper: { sx: {
        maxWidth: 800,
        m: { xs: 1, sm: 2 },
        width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
        maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
        backgroundImage: "none",
        bgcolor: (theme) => dialogSurfaceColors[theme.palette.mode].paper,
        border: 1,
        borderColor: "divider"
      } } }}
    >
      <DefaultPropsProvider value={{
        MuiTextField: { size: "small" },
        MuiAutocomplete: { size: "small" },
        MuiSwitch: { size: "small" }
      }}>
      <DialogTitle sx={{ px: 2.5, py: 1.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.02em" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
          <Box display="inline-flex" alignItems="center" gap={1}>
            <EditNoteIcon sx={{ color: "primary.main" }} />
            {configReadOnly ? "Stream configuration" : editingStream ? "Edit stream" : "Add stream"}
          </Box>
          {recastAvailable ? (
            <RadioGroup
              row
              aria-label="Stream mode"
              name="stream-mode"
              sx={{
                gap: 0.5,
                "& .MuiFormControlLabel-root": { m: 0 },
                "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" },
                "& .MuiRadio-root": { p: 0.75 }
              }}
              value={streamMode}
              onChange={(event) => {
                if (configReadOnlyRef.current) return;
                const nextMode = normalizeStreamMode(event.target.value);
                setStreamMode(nextMode);
                setModeDirty(nextMode !== initialMode);
              }}
            >
              {STREAM_MODE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  disabled={configReadOnly}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          ) : null}
        </Stack>
      </DialogTitle>
      <Formik
        initialValues={
          editingStream
            ? {
                sensor: editingStream.sensor,
                platformId: editingStream.platformId ?? "",
                type: (editingStream.type ?? STREAM_TYPE_OPTIONS[0].value).toLowerCase(),
                url: editingStream.url,
                networkInterface: editingStream.networkInterface ?? "",
                description: editingStream.description ?? "",
                payloadType:
                  (editingStream.payloadType ?? DEFAULT_PAYLOAD_TYPE)?.toString().trim().toLowerCase() ||
                  DEFAULT_PAYLOAD_TYPE,
                active: editingStream.active,
                autoStart: editingStream.autoStart ?? false,
                passthrough: editingStream.passthrough ?? false,
                video: mapStreamVideoSettingsToFormValues(editingStream.live, defaultVideoBitrate),
                srt: mapStreamSrtSettingsToFormValues(editingStream.srt),
                uds: mapStreamUdsSettingsToFormValues(editingStream.uds),
                rtsp: mapStreamRtspSettingsToFormValues(editingStream.rtsp),
                file: mapStreamFileSettingsToFormValues(editingStream.file),
                pcap: mapStreamPcapSettingsToFormValues(editingStream.pcap),
                timeout: editingStream.timeout ?? DEFAULT_TIMEOUT,
                offlineAfter: editingStream.offlineAfter ?? DEFAULT_OFFLINE_AFTER,
                bufferSizeKb: bufferBytesToKb(
                  editingStream.bufferSize ?? DEFAULT_BUFFER_SIZE_BYTES
                ),
                lowLatency: Boolean(editingStream.lowLatency),
                cacheMs: editingStream.cacheMs ?? DEFAULT_CACHE_MS,
                addPlatformMetadata: editingStream.platformId
                  ? Boolean(editingStream.addPlatformMetadata)
                  : false,
                targets: mapStreamTargetsToFormValues(editingStream.targets),
                processors: (editingStream.processors ?? []).map((processor) => ({ ...processor, engines: (processor.engines ?? []).map((engine) => ({ ...engine, config: isRecord(engine.config) ? { ...engine.config } : engine.config })) })),
                flywheel: mapFlywheelToFormValues(editingStream.processors)
              }
            : createDefaultFormValues(defaultVideoBitrate)
        }
        enableReinitialize
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (configReadOnlyRef.current) {
            setSubmitting(false);
            return;
          }
          setDialogError(null);
          let streamsInvalidated = false;
          const ensureInvalidateStreams = () => {
            if (streamsInvalidated) {
              return;
            }
            invalidateStreams();
            streamsInvalidated = true;
          };
          const normalizedStreamType = (values.type ?? "").trim().toLowerCase();
          let liveSettings: StreamVideoSettings | undefined;
          if (normalizedStreamType !== "pattern" && !values.passthrough) {
            liveSettings = { streams: buildVideoStreamsPayload(values.video, defaultVideoBitrate) };
          }
          const srtSettingsPayload =
            normalizedStreamType === "srt" ? buildSrtSettingsPayload(values.srt) : undefined;
          const udsSettingsPayload =
            normalizedStreamType === "uds" ? buildUdsSettingsPayload(values.uds) : undefined;
          const rtspSettingsPayload =
            normalizedStreamType === "rtsp" ? buildRtspSettingsPayload(values.rtsp) : undefined;
          const fileSettingsPayload =
            normalizedStreamType === "file" ? buildFileSettingsPayload(values.file) : undefined;
          const pcapSettingsPayload = normalizedStreamType === "pcap" ? buildPcapSettingsPayload(values.pcap) : undefined;
          const effectiveMode = resolveEffectiveMode();
          const recastEnabled = effectiveMode === "recast" || effectiveMode === "playAndRecast";
          const targetsPayload = recastEnabled ? buildStreamTargetsPayload(values.targets) : undefined;
          const processorsPayload = buildProcessorsPayload(values.processors, values.flywheel);
          try {
            const trimmedPlatformId = values.platformId.trim();
            const timeout = typeof values.timeout === "number" ? values.timeout : DEFAULT_TIMEOUT;
            const offlineAfter =
              typeof values.offlineAfter === "number" ? values.offlineAfter : DEFAULT_OFFLINE_AFTER;
            const bufferSize = bufferKbToBytes(
              typeof values.bufferSizeKb === "number"
                ? values.bufferSizeKb
                : DEFAULT_BUFFER_SIZE_KB
            );
            const cacheMs =
              typeof values.cacheMs === "number" ? values.cacheMs : DEFAULT_CACHE_MS;
            const payloadType =
              (values.payloadType || DEFAULT_PAYLOAD_TYPE)?.toString().trim().toLowerCase() ||
              DEFAULT_PAYLOAD_TYPE;
            const payload = {
              sensor: values.sensor.trim(),
              platformId: trimmedPlatformId,
              type: normalizedStreamType,
              url: normalizedStreamType === "pcap" ? `pcap:///${values.pcap.file.trim()}` : values.url.trim(),
              networkInterface: values.networkInterface.trim(),
              description: values.description.trim(),
              payloadType,
              active: values.active,
              autoStart: values.autoStart,
              passthrough: normalizedStreamType === "pattern" ? true : values.passthrough,
              mode: effectiveMode,
              timeout,
              offlineAfter,
              bufferSize,
              lowLatency: normalizedStreamType === "pattern" ? false : values.lowLatency,
              cacheMs,
              addPlatformMetadata: trimmedPlatformId ? values.addPlatformMetadata : false,
              ...(liveSettings ? { live: liveSettings } : {}),
              ...(targetsPayload && targetsPayload.length > 0 ? { targets: targetsPayload } : {}),
              ...(processorsPayload.length > 0 ? { processors: processorsPayload } : {}),
              ...(srtSettingsPayload ? { srt: srtSettingsPayload } : {}),
              ...(udsSettingsPayload ? { uds: udsSettingsPayload } : {}),
              ...(rtspSettingsPayload ? { rtsp: rtspSettingsPayload } : {}),
              ...(fileSettingsPayload ? { file: fileSettingsPayload } : {}),
              ...(pcapSettingsPayload ? { pcap: pcapSettingsPayload } : {})
            };
            const response = await authorizedFetch(
              editingStream ? `/api/streams/${editingStream.id}` : "/api/streams",
              {
                method: editingStream ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
              }
            );

            if (!response.ok) {
              const data = await response.json().catch(() => null);
              const message = data?.error ?? "Unable to save stream";
              // Creation can succeed even when runtime start fails; refetch to surface the new stream.
              ensureInvalidateStreams();
              throw new Error(message);
            }

            // Close as soon as the save is confirmed. Stream invalidation and
            // snackbar updates must not keep a successfully saved form open.
            onClose();
            ensureInvalidateStreams();
            onShowSnackbar({
              open: true,
              message: editingStream ? `${values.sensor} updated.` : `${values.sensor} added.`,
              severity: "success"
            });
          } catch (err) {
            console.error(err);
            const message = err instanceof Error ? err.message : "Unable to save stream.";
            setActiveTab("general");
            ensureInvalidateStreams();
            setDialogError(message);
            onShowSnackbar({
              open: true,
              message,
              severity: "error"
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange: changeField,
          handleBlur,
          isSubmitting,
          setFieldValue: updateFieldValue,
          dirty,
          isValid
        }) => {
          const setFieldValue: typeof updateFieldValue = (...args) => configReadOnlyRef.current
            ? Promise.resolve()
            : updateFieldValue(...args);
          const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (!configReadOnlyRef.current) changeField(event);
          };
          const normalizedStreamType = (values.type ?? "").trim().toLowerCase();
          const isFileType = normalizedStreamType === "file";
          const isPcapType = normalizedStreamType === "pcap";
 const isPatternType=normalizedStreamType==="pattern";
          const isStreamTabVisible = !isFileType && !isPcapType && !isPatternType;
          const isFileTabVisible = isFileType;
          const isPcapTabVisible = isPcapType;
          const isSrtTabVisible = normalizedStreamType === "srt";
          const isUdsTabVisible = normalizedStreamType === "uds";
          const isRtspTabVisible = normalizedStreamType === "rtsp";
          const effectiveMode = resolveEffectiveMode();
          const isRecastOnlyMode = effectiveMode === "recast";
          const showPlaybackCache = !isRecastOnlyMode;
          const showLowLatency = showPlaybackCache && !isPatternType;
          const showPassthroughField = !isRecastOnlyMode && !isPatternType;
          const isVideoTabVisible = showPassthroughField && !values.passthrough;
          const recastEnabled = effectiveMode === "recast" || effectiveMode === "playAndRecast";
          const showPayloadTypeField = effectiveMode === "recast";
          const isOutputsTabVisible = recastEnabled;
          const hasExistingFlywheel = Boolean(values.flywheel.processorId);
          const isFlywheelEligible = recastEnabled && ["udp", "srt", "uds"].includes(normalizedStreamType) && values.payloadType === "mpegts";
          const isFlywheelTabVisible = isFlywheelEligible || hasExistingFlywheel;
          const flywheelUsable = recastAvailable && serverConfig.flywheelLicensed && serverConfig.flywheelAvailable && isFlywheelEligible;
          const flywheelReadOnlyReason = flywheelUsable ? undefined : !serverConfig.flywheelLicensed ? "The Flywheel license option is required." : !serverConfig.flywheelAvailable ? (serverConfig.flywheelUnavailableReason ?? "The native Flywheel worker is unavailable.") : !isFlywheelEligible ? "Flywheel requires a UDP, SRT, or UDS MPEG-TS stream in Recast mode." : undefined;
          const availableTabs: StreamFormTab[] = ["general"];
          if (isStreamTabVisible) {
            availableTabs.push("stream");
          }
          if (isFileTabVisible) {
            availableTabs.push("file");
          }
          if (isPcapTabVisible) availableTabs.push("pcap");
          if (isSrtTabVisible) {
            availableTabs.push("srt");
          }
          if (isUdsTabVisible) {
            availableTabs.push("uds");
          }
          if (isRtspTabVisible) {
            availableTabs.push("rtsp");
          }
          if (isVideoTabVisible) {
            availableTabs.push("video");
          }
          if (isFlywheelTabVisible) {
            availableTabs.push("flywheel");
          }
          if (isOutputsTabVisible) {
            availableTabs.push("outputs");
          }
          const tabValue: StreamFormTab = availableTabs.includes(activeTab) ? activeTab : "general";
          const videoCodecError = getIn(errors, ["video", "codec"]);
          const videoCodecTouched = getIn(touched, ["video", "codec"]);
          const videoCodecHelperText =
            videoCodecTouched && typeof videoCodecError === "string" ? videoCodecError : undefined;
          const videoCodecHasError = Boolean(videoCodecTouched && videoCodecError);
          const formDisabled = configReadOnly;
          const requiresOutputs = recastEnabled;
 const patternInvalid=isPatternType && (!recastAvailable || Boolean(validatePattern(values.url)) || values.flywheel.enabled || values.targets.some(t=>t.inputType==="processor" || t.type==="rawPcap"));
          const hasOutputsConfigured = values.targets.length > 0;
          const submitDisabled =
            isSubmitting ||
            isCaptureUploading ||
            isMediaUploading ||
            !isValid ||
            formDisabled ||
            (editingStream ? !dirty && !modeDirty : false) ||
            (requiresOutputs && !hasOutputsConfigured) || patternInvalid;
          const submitProgressText = editingStream
            ? "Saving stream configuration. Waiting for backend to apply changes..."
            : "Creating stream. Waiting for backend to apply changes...";
          const missingRequirements = collectRequiredValidationMessages(errors);
          if (requiresOutputs && !hasOutputsConfigured) {
            missingRequirements.push("At least one output target is required");
          }
          const missingRequirementsText =
            missingRequirements.length > 0 ? `Missing: ${Array.from(new Set(missingRequirements)).join(" | ")}` : "";
          const urlProtocolPrefix = getTypeProtocolPrefix(values.type);
          const displayedUrlProtocolPrefix = isPcapType ? "pcap:///" : urlProtocolPrefix;
          const isPlatformSelected = Boolean(values.platformId.trim());
          const urlFieldValue = isPcapType
            ? values.pcap.file
            : urlProtocolPrefix
            ? stripTypeProtocolFromUrl(isPatternType?visiblePatternUrl(values.url):values.url, urlProtocolPrefix)
            : values.url;
          const urlHelperText = ["udp", "srt"].includes(normalizedStreamType)
            ? "Enter host:port, or :port to listen on all interfaces."
            : undefined;
          const applyPcapFlow = (flow: PcapUDPFlow) => {
            if (configReadOnlyRef.current) return;
            setFieldValue("pcap.sourceIp", flow.sourceIp);
            setFieldValue("pcap.destinationIp", flow.destinationIp);
            setFieldValue("pcap.sourcePort", flow.sourcePort);
            setFieldValue("pcap.destinationPort", flow.destinationPort);
          };
          const inspectPcapFile = async (path: string, hostMediaDrive = "") => {
            if (configReadOnlyRef.current) return;
            const requestId = ++pcapInspectRequestRef.current;
            setPcapFlows([]);
            setPcapInspectError(null);
            if (!path) {
              setIsPcapInspecting(false);
              return;
            }
            setIsPcapInspecting(true);
            try {
              const query = new URLSearchParams({ path });
              if (hostMediaDrive) query.set("hostMediaDrive", hostMediaDrive);
              const response = await authorizedFetch(`/api/capture-files/flows?${query.toString()}`);
              const payload = (await response.json().catch(() => null)) as
                | { flows?: PcapUDPFlow[]; error?: string }
                | null;
              if (!response.ok) {
                throw new Error(payload?.error ?? "Unable to inspect capture file");
              }
              if (requestId !== pcapInspectRequestRef.current) return;
              const flows = payload?.flows ?? [];
              setPcapFlows(flows);
              if (flows.length > 0) {
                applyPcapFlow(flows[0]);
              } else {
                setPcapInspectError("No UDP streams were found in this capture file.");
              }
            } catch (error) {
              if (requestId !== pcapInspectRequestRef.current) return;
              setPcapInspectError(error instanceof Error ? error.message : "Unable to inspect capture file");
            } finally {
              if (requestId === pcapInspectRequestRef.current) setIsPcapInspecting(false);
            }
          };
          const handleCaptureUpload = async (event: ChangeEvent<HTMLInputElement>) => {
            if (configReadOnlyRef.current) return;
            const input = event.currentTarget;
            const file = event.target.files?.[0];
            if (!file) return;

            // The managed upload path is the selected basename. Reflect it in the
            // form immediately; large capture uploads can otherwise make the file
            // selection appear to have done nothing for several seconds.
            setFieldValue("pcap.file", file.name);
            setFieldValue("pcap.hostMediaDrive", "");
            setFieldValue("url", `pcap:///${file.name}`);
            setPcapFlows([]);
            setPcapInspectError(null);

            setIsCaptureUploading(true);
            setCaptureUploadProgress(0);
            setCaptureUploadError(null);
            try {
              const payload = await new Promise<{ path: string }>((resolve, reject) => {
                const formData = new FormData();
                formData.append("file", file);
                const xhr = new XMLHttpRequest();
                xhr.open("POST", resolveApiPath("/api/capture-files/upload"));
                if (accessToken) {
                  xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
                }
                xhr.upload.onprogress = (progressEvent) => {
                  if (progressEvent.lengthComputable) {
                    setCaptureUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                  }
                };
                xhr.onerror = () => reject(new Error("Capture upload failed due to a network error"));
                xhr.onload = () => {
                  const responsePayload = (() => {
                    try {
                      return JSON.parse(xhr.responseText) as { path?: string; error?: string };
                    } catch {
                      return null;
                    }
                  })();
                  if (xhr.status >= 200 && xhr.status < 300 && responsePayload?.path) {
                    resolve({ path: responsePayload.path });
                    return;
                  }
                  reject(new Error(responsePayload?.error ?? "Unable to upload capture file"));
                };
                xhr.send(formData);
              });
              const path = payload.path;
              setCaptureUploadProgress(100);
              setFieldValue("pcap.file", path);
              setFieldValue("pcap.hostMediaDrive", "");
              setFieldValue("url", `pcap:///${path}`);
              void inspectPcapFile(path);
            } catch (error) {
              setCaptureUploadError(error instanceof Error ? error.message : "Unable to upload capture file");
            } finally {
              setIsCaptureUploading(false);
              // Keep the backing File alive until fetch has consumed the body.
              // Clearing this before fetch completes produces zero-byte uploads
              // in the desktop WebView.
              input.value = "";
            }
          };
          const handleCaptureFileSelect = async () => {
            if (configReadOnlyRef.current) return;
            const desktopBridge = (window as unknown as DesktopCaptureBridge).go?.main?.DesktopApp;
            if (!isDesktopShell || !desktopBridge?.SelectCaptureFile || !desktopBridge.ImportCaptureFile) {
              captureUploadInputRef.current?.click();
              return;
            }

            setCaptureUploadError(null);
            try {
              const selected = await desktopBridge.SelectCaptureFile();
              if (configReadOnlyRef.current) return;
              if (!selected.sourcePath || !selected.name) return;

              setFieldValue("pcap.file", selected.name);
              setFieldValue("pcap.hostMediaDrive", "");
              setFieldValue("url", `pcap:///${selected.name}`);
              setPcapFlows([]);
              setPcapInspectError(null);
              setCaptureUploadProgress(0);
              setIsCaptureUploading(true);
              await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
              if (configReadOnlyRef.current) return;

              const imported = await desktopBridge.ImportCaptureFile(selected.sourcePath);
              if (!imported.path) return;
              setCaptureUploadProgress(100);
              setFieldValue("pcap.file", imported.path);
              setFieldValue("pcap.hostMediaDrive", "");
              setFieldValue("url", `pcap:///${imported.path}`);
              void inspectPcapFile(imported.path);
            } catch (error) {
              setCaptureUploadError(error instanceof Error ? error.message : "Unable to import capture file");
            } finally {
              setIsCaptureUploading(false);
            }
          };
          const handleMediaUpload = async (event: ChangeEvent<HTMLInputElement>) => {
            if (configReadOnlyRef.current) return;
            const input = event.currentTarget;
            const file = event.target.files?.[0];
            if (!file) return;

            setIsMediaUploading(true);
            setMediaUploadProgress(0);
            setMediaUploadError(null);
            try {
              const payload = await new Promise<{ fullPath: string }>((resolve, reject) => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("path", "");
                const xhr = new XMLHttpRequest();
                xhr.open("POST", resolveApiPath("/api/server/host-media/upload"));
                if (accessToken) xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
                xhr.upload.onprogress = (progressEvent) => {
                  if (progressEvent.lengthComputable) {
                    setMediaUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                  }
                };
                xhr.onerror = () => reject(new Error("Media upload failed due to a network error"));
                xhr.onload = () => {
                  let responsePayload: { fullPath?: string; error?: string } | null = null;
                  try {
                    responsePayload = JSON.parse(xhr.responseText) as { fullPath?: string; error?: string };
                  } catch {
                    responsePayload = null;
                  }
                  if (xhr.status >= 200 && xhr.status < 300 && responsePayload?.fullPath) {
                    resolve({ fullPath: responsePayload.fullPath });
                  } else {
                    reject(new Error(responsePayload?.error ?? "Unable to upload media file"));
                  }
                };
                xhr.send(formData);
              });
              setMediaUploadProgress(100);
              setFieldValue("url", buildFileUrlFromPath(payload.fullPath));
            } catch (error) {
              setMediaUploadError(error instanceof Error ? error.message : "Unable to upload media file");
            } finally {
              setIsMediaUploading(false);
              input.value = "";
            }
          };
          const handleMediaFileSelect = async () => {
            if (configReadOnlyRef.current) return;
            const desktopBridge = (window as unknown as DesktopCaptureBridge).go?.main?.DesktopApp;
            if (!desktopBridge?.SelectMediaFile) {
              onShowSnackbar({
                open: true,
                message: "Native file selection is unavailable.",
                severity: "error"
              });
              return;
            }

            try {
              const path = await desktopBridge.SelectMediaFile();
              if (!path) return;
              setFieldValue("url", buildFileUrlFromPath(path));
            } catch (error) {
              onShowSnackbar({
                open: true,
                message: error instanceof Error ? error.message : "Unable to select media file.",
                severity: "error"
              });
            }
          };
          const renderPcapProgress = () => {
            if (!isCaptureUploading && !isPcapInspecting) return null;
            const hasUploadPercentage = isCaptureUploading && captureUploadProgress > 0;
            const status = isCaptureUploading
              ? hasUploadPercentage
                ? `Importing capture file (${captureUploadProgress}%)`
                : "Importing capture file..."
              : "Inspecting UDP streams...";
            return (
              <Stack spacing={0.5} sx={{ width: "100%" }}>
                <LinearProgress
                  variant={hasUploadPercentage ? "determinate" : "indeterminate"}
                  value={hasUploadPercentage ? captureUploadProgress : undefined}
                  aria-label={status}
                />
                <Typography variant="caption" color="text.secondary">{status}</Typography>
              </Stack>
            );
          };
          const renderPlaybackCacheField = () => showPlaybackCache ? (
            <Tooltip
              title={CACHE_TOOLTIP}
              placement="top"
              arrow
              disableInteractive
            >
              <TextField
                name="cacheMs"
                label="Cache (ms)"
                type="number"
                value={values.cacheMs}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setFieldValue("cacheMs", nextValue === "" ? "" : Number(nextValue));
                }}
                onBlur={handleBlur}
                error={touched.cacheMs && Boolean(errors.cacheMs)}
                helperText={touched.cacheMs ? errors.cacheMs : undefined}
                slotProps={{
                  htmlInput: {
                    min: MIN_CACHE_MS,
                    max: MAX_CACHE_MS,
                    step: 1
                  }
                }}
                disabled={formDisabled || (showLowLatency && values.lowLatency)}
                sx={{ width: { xs: "100%", sm: 240 }, minWidth: 0 }}
              />
            </Tooltip>
          ) : null;
          return (
            <Form onSubmitCapture={(event) => {
              if (configReadOnlyRef.current) {
                event.preventDefault();
                event.stopPropagation();
              }
            }} style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
              <input
                ref={captureUploadInputRef}
                type="file"
                accept=".pcap,.pcapng,application/vnd.tcpdump.pcap"
                hidden
                onChange={handleCaptureUpload}
              />
              <input
                ref={mediaUploadInputRef}
                type="file"
                hidden
                onChange={handleMediaUpload}
              />
              <DialogContent dividers sx={{
                px: { xs: 1.5, sm: 2.5 },
                pt: 0.5,
                pb: 1.5,
                bgcolor: (theme) => dialogSurfaceColors[theme.palette.mode].content,
                "& .MuiFormControlLabel-label": { fontSize: "0.875rem" }
              }}>
                <Stack spacing={1.5}>
                  {configReadOnly ? <Alert severity="info">Read-only configuration. Close this dialog and stop the stream to edit.</Alert> : null}
                  {isPlatformsError ? (
                    <Alert severity="warning">
                      {platformsError instanceof Error
                        ? platformsError.message
                        : "Unable to load platforms. You can still save the stream without a platform."}
                    </Alert>
                  ) : null}
                  <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => {
                      setActiveTab(newValue as StreamFormTab);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Stream configuration"
                    sx={{
                      minHeight: 40,
                      borderBottom: 1,
                      borderColor: "divider",
                      "& .MuiTab-root": { minHeight: 40, minWidth: 0, px: 1.75, py: 1, fontSize: "0.875rem", textTransform: "none", fontWeight: 600 }
                    }}
                  >
                    <Tab label="General" value="general" />
                    {isFileType ? (
                      <Tab label="Source File" value="file" />
                    ) : isPcapType ? (
                      <Tab label="Source File" value="pcap" />
                    ) : !isPatternType ? (
                      <Tab label="Source Stream" value="stream" />
                    ) : null}
                    {isSrtTabVisible ? <Tab label="SRT" value="srt" /> : null}
                    {isUdsTabVisible ? <Tab label="UDS" value="uds" /> : null}
                    {isRtspTabVisible ? <Tab label="RTSP" value="rtsp" /> : null}
                    {isVideoTabVisible ? <Tab label="Video" value="video" /> : null}
                    {isFlywheelTabVisible ? <Tab label="Flywheel" value="flywheel" /> : null}
                    {isOutputsTabVisible ? <Tab label="Outputs" value="outputs" /> : null}
                  </Tabs>
                  {tabValue === "general" ? (
                    <Stack spacing={1.5}>
                      <Stack spacing={1.5} sx={{ p: 1.5, bgcolor: (theme) => dialogSurfaceColors[theme.palette.mode].section, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>Source</Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" }, gap: 1.5 }}>
                      <TextField
                        name="sensor"
                        label="Sensor"
                        fullWidth
                        sx={{ gridColumn: "1 / -1" }}
                        value={values.sensor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.sensor && Boolean(errors.sensor)}
                        helperText={touched.sensor ? errors.sensor : undefined}
                        disabled={formDisabled}
                      />
                        <TextField
                          name="platformId"
                          label="Platform"
                          select
                          fullWidth
                          value={values.platformId}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            handleChange(event);
                            const nextPlatformId = (event.target.value ?? "").trim();
                            if (!nextPlatformId) {
                              setFieldValue("addPlatformMetadata", false);
                            }
                          }}
                          onBlur={handleBlur}
                          error={touched.platformId && Boolean(errors.platformId)}
                          helperText={touched.platformId ? errors.platformId : undefined}
                          disabled={formDisabled || isPlatformsLoading}
                          sx={{ flexGrow: 1 }}
                        >
                          <MenuItem value="">
                            <em>No platform</em>
                          </MenuItem>
                          {platformOptions.map((platform) => (
                            <MenuItem key={platform.id} value={platform.id}>
                              {platform.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <FormControlLabel
                          sx={{
                            m: 0,
                            justifySelf: { xs: "start", sm: "end" },
                            alignSelf: "center",
                            "& .MuiFormControlLabel-label": { fontSize: "0.8125rem", whiteSpace: "nowrap" }
                          }}
                          control={
                            <Switch
                              checked={values.addPlatformMetadata}
                              onChange={(event) => {
                                setFieldValue("addPlatformMetadata", event.target.checked);
                              }}
                              name="addPlatformMetadata"
                              disabled={!isPlatformSelected || formDisabled}
                            />
                          }
                          label="Add Platform Metadata"
                          disabled={!isPlatformSelected || formDisabled}
                        />
                      </Box>
                      <Stack
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Stack direction="row" spacing={0} alignItems="flex-start" sx={{
                          minWidth: 0,
                          width: "100%",
                          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                          "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" }
                        }}>
                        <TextField
                          name="type"
                          label="Protocol"
                          select
                          value={values.type}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const previousProtocol = getTypeProtocolPrefix(values.type);
                            const urlSuffix = stripTypeProtocolFromUrl(values.url, previousProtocol);
                            handleChange(event);
                            setHostMediaDialogOpen(false);
                            const nextProtocol = getTypeProtocolPrefix(event.target.value);
                            const sanitizedSuffix = urlSuffix.replace(/^\s+/, "");
                            if (nextProtocol) {
                              setFieldValue("url", event.target.value==="pattern" ? `pattern://${patternValue(parsePattern())}` : sanitizedSuffix ? `${nextProtocol}${sanitizedSuffix}` : "");
 if(event.target.value==="pattern") {
   setFieldValue("payloadType", "mpegts");
   setFieldValue("passthrough", true);
   setFieldValue("lowLatency", false);
 }
                            } else {
                              setFieldValue("url", sanitizedSuffix);
                            }
                          }}
                          onBlur={handleBlur}
                          error={touched.type && Boolean(errors.type)}
                          helperText={touched.type ? errors.type : undefined}
                          sx={{
                            width: { xs: 108, sm: 128 },
                            flexShrink: 0,
                            "& .MuiOutlinedInput-root": { borderRadius: "12px 0 0 12px" },
                            "& .MuiOutlinedInput-notchedOutline": { borderRightWidth: 0 }
                          }}
                          disabled={formDisabled}
                        >
                          {STREAM_TYPE_OPTIONS.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              disabled={
                                option.value !== "udp" &&
                                option.value !== "srt" &&
                                option.value !== "rtsp" &&
                                option.value !== "uds" &&
                                option.value !== "file" &&
                                option.value !== "pcap" &&
 (option.value !== "pattern" || !recastAvailable || !serverConfig.patternAvailable)
                              }
                            >
                              <Tooltip title={option.tooltip} placement="right" enterDelay={300} arrow>
                                <Box component="span" display="inline-flex">
                                  {option.label}
                                </Box>
                              </Tooltip>
                            </MenuItem>
                          ))}
                        </TextField>
                          <TextField
                            name="url"
                            label="Address / Path"
                            fullWidth
                            value={urlFieldValue}
                            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                              if (!urlProtocolPrefix) {
                                const sanitizedValue = event.target.value.replace(/^\s+/, "");
                                if (sanitizedValue !== event.target.value) {
                                  setFieldValue("url", sanitizedValue);
                                } else {
                                  handleChange(event);
                                }
                                return;
                              }
                              const sanitizedValue = event.target.value.replace(/^\s+/, "");
                              const lowerValue = sanitizedValue.toLowerCase();
                              const lowerPrefix = urlProtocolPrefix.toLowerCase();
                              const withoutPrefix = lowerValue.startsWith(lowerPrefix)
                                ? sanitizedValue.slice(urlProtocolPrefix.length)
                                : sanitizedValue;
                              const sanitizedSuffix = withoutPrefix.replace(/^\s+/, "");
                              const nextUrl = sanitizedSuffix ? `${urlProtocolPrefix}${sanitizedSuffix}` : "";
                              setFieldValue("url", isPatternType?withHiddenPatternKlv(values.url,nextUrl):nextUrl);
                            }}
                            onBlur={handleBlur}
                            error={touched.url && Boolean(errors.url)}
                            helperText={touched.url && errors.url ? errors.url : urlHelperText}
                            disabled={formDisabled || isPcapType}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              "& .MuiOutlinedInput-root": { borderRadius: "0 12px 12px 0" }
                            }}
                            InputProps={{
                              startAdornment: displayedUrlProtocolPrefix ? (
                                <InputAdornment position="start" disableTypography sx={{ color: "text.secondary", fontSize: "inherit", mr: 0 }}>
                                  {displayedUrlProtocolPrefix}
                                </InputAdornment>
                              ) : undefined,
                              endAdornment: isFileType || isPcapType ? (
                                <InputAdornment position="end">
                                  {isDesktopShell ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Tooltip title="Select file" placement="top" arrow>
                                        <span>
                                          <IconButton
                                            aria-label="Select file"
                                            size="small"
                                            disabled={formDisabled}
                                            onClick={() => {
                                              if (isPcapType) {
                                                setCaptureUploadError(null);
                                                void handleCaptureFileSelect();
                                              } else {
                                                void handleMediaFileSelect();
                                              }
                                            }}
                                          >
                                            <FolderOpenIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  ) : (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Tooltip title="Select local file" placement="top" arrow>
                                        <span>
                                          <IconButton
                                            aria-label="Select local file"
                                            size="small"
                                            disabled={formDisabled || isCaptureUploading || isMediaUploading}
                                            onClick={() => {
                                              if (isPcapType) {
                                                setCaptureUploadError(null);
                                                captureUploadInputRef.current?.click();
                                              } else {
                                                setMediaUploadError(null);
                                                mediaUploadInputRef.current?.click();
                                              }
                                            }}
                                          >
                                            <UploadFileIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Select server file" placement="top" arrow>
                                        <span>
                                          <IconButton
                                            aria-label="Select server file"
                                            size="small"
                                            disabled={formDisabled || isCaptureUploading || isMediaUploading}
                                            onClick={() => setHostMediaDialogOpen(true)}
                                          >
                                            <FolderOpenIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  )}
                                </InputAdornment>
                              ) : undefined
                            }}
                          />
                        </Stack>
                        {(() => {
                          const showNetworkInterfaceField =
                            normalizedStreamType === "udp" && isMulticastUrl(values.url);
                          if (!formDisabled && !showNetworkInterfaceField && values.networkInterface.trim()) {
                            setFieldValue("networkInterface", "");
                          }

                          return showNetworkInterfaceField ? (
                            <Autocomplete
                              freeSolo
                              options={networkInterfaceOptions}
                              value={values.networkInterface}
                              disabled={formDisabled}
                              onOpen={() => {
                                void loadNetworkInterfaces(values.networkInterface);
                              }}
                              onChange={(_, newValue) => {
                                setFieldValue("networkInterface", (newValue ?? "").trim());
                              }}
                              onInputChange={(_, newInputValue, reason) => {
                                if (reason === "input") {
                                  setFieldValue("networkInterface", newInputValue);
                                }
                              }}
                              loading={isNetworkInterfacesLoading}
                              sx={{ width: { xs: "100%", sm: 250 }, flexShrink: 0 }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="networkInterface"
                                  label="Network interface"
                                  fullWidth
                                  onBlur={handleBlur}
                                  error={touched.networkInterface && Boolean(errors.networkInterface)}
                                  helperText={touched.networkInterface ? errors.networkInterface : undefined}
                                  disabled={formDisabled}
                                />
                              )}
                            />
                          ) : null;
                        })()}
                      </Stack>
                      </Stack>
                      {isPatternType && <Stack spacing={1.5} sx={{ px: 1.5, pt: 0.5, pb: 1.5, bgcolor: (theme) => dialogSurfaceColors[theme.palette.mode].section, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                       {!recastAvailable && <Alert severity="warning">Pattern requires Recast.</Alert>}
                       {!serverConfig.patternAvailable && <Alert severity="warning">{serverConfig.patternUnavailableReason || "Pattern worker unavailable"}</Alert>}
                       {(values.flywheel.enabled || values.targets.some(t=>t.inputType==="processor" || t.type==="rawPcap")) && <Alert severity="error">Disable Flywheel and change all outputs to original-source UDP, SRT or UDS before saving Pattern.</Alert>}
                       <PatternParams streamId={editingStream?.id} value={values.url} onChange={v=>{ if (!configReadOnlyRef.current) setFieldValue("url",`pattern://${v}`); }} authorizedFetch={authorizedFetch} disabled={formDisabled || !recastAvailable} readOnly={configReadOnly}/>
                      </Stack>}
                      {isPcapType ? renderPcapProgress() : null}
                      {isFileType && isMediaUploading ? (
                        <Stack spacing={0.5}>
                          <LinearProgress variant={mediaUploadProgress > 0 ? "determinate" : "indeterminate"} value={mediaUploadProgress > 0 ? mediaUploadProgress : undefined} />
                          <Typography variant="caption" color="text.secondary">
                            {mediaUploadProgress > 0 ? `Uploading media file (${mediaUploadProgress}%)` : "Uploading media file..."}
                          </Typography>
                        </Stack>
                      ) : null}
                      {isFileType && mediaUploadError ? <Alert severity="error">{mediaUploadError}</Alert> : null}
                      <Stack spacing={1.5} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: (theme) => dialogSurfaceColors[theme.palette.mode].section }}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600, mb: -1 }}>Behavior & notes</Typography>
                      {(showPassthroughField || (!isRecastOnlyMode && !isPatternType && !values.passthrough) || showLowLatency) && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ sm: "center" }}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {showPassthroughField ? (
                          <Tooltip title="Passthrough video for playback" placement="top" arrow>
                            <span>
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Switch
                                    checked={values.passthrough}
                                    onChange={(event) => {
                                      const checked = event.target.checked;
                                      if (!checked && values.passthrough) {
                                        const hasExistingLiveStreams = Boolean(editingStream?.live?.streams?.length);
                                        if (!hasExistingLiveStreams) {
                                          setFieldValue("video", createDefaultVideoParams({ bitrate: defaultVideoBitrate }));
                                        }
                                      }
                                      setFieldValue("passthrough", checked);
                                      if (checked && activeTab === "video") {
                                        setActiveTab("general");
                                      }
                                    }}
                                    name="passthrough"
                                    disabled={formDisabled}
                                  />
                                }
                                label="Passthrough"
                              />
                            </span>
                          </Tooltip>
                        ) : null}
                        {showPassthroughField
                          ? (() => {
                              const requiresPassthrough =
                                (values.payloadType ?? "").trim().toLowerCase() === "data" && !values.passthrough;
                              return requiresPassthrough ? (
                                <Typography color="error" variant="caption" sx={{ ml: 1 }}>
                                  Payload type "Data" requires stream Passthrough
                                </Typography>
                              ) : null;
                            })()
                          : null}
                        {!isRecastOnlyMode && !isPatternType && !values.passthrough ? (
                          <TextField
                            name="video.codec"
                            label="Video codec"
                            select
                            value={values.video.codec}
                            onChange={(event) => {
                              const nextCodec = event.target.value as VideoParamsFormValues["codec"];
                              setFieldValue("video.codec", nextCodec);
                            }}
                            onBlur={handleBlur}
                            error={videoCodecHasError}
                            helperText={videoCodecHelperText}
                            sx={{ minWidth: { sm: 100 }, width: { sm: 120 } }}
                            disabled={formDisabled}
                          >
                            {VIDEO_CODEC_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : null}
                        {showLowLatency ? (
                          <Tooltip
                            title="Remove the playback cache for the lowest possible latency. Source timing jitter may become visible."
                            placement="top"
                            arrow
                          >
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Switch
                                  checked={values.lowLatency}
                                  onChange={(event) => {
                                    setFieldValue("lowLatency", event.target.checked);
                                  }}
                                  name="lowLatency"
                                  disabled={formDisabled}
                                />
                              }
                              label="Low Latency"
                            />
                          </Tooltip>
                        ) : null}

                      </Stack>
                      )}
                      <TextField
                        name="description"
                        label="Description"
                        fullWidth
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={touched.description ? errors.description : undefined}
                        disabled={formDisabled}
                      />
                      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ "& .MuiFormControlLabel-root": { m: 0 } }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.active}
                              onChange={(event) => setFieldValue("active", event.target.checked)}
                              name="active"
                              disabled={formDisabled}
                            />
                          }
                          label="Active"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.autoStart}
                              onChange={(event) => setFieldValue("autoStart", event.target.checked)}
                              name="autoStart"
                              disabled={formDisabled || !values.active}
                            />
                          }
                          label="Auto-start"
                        />
                      </Stack>
                      </Stack>
                      {missingRequirementsText ? (
                        <Typography variant="body2" color="warning.main">
                          {missingRequirementsText}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                  {tabValue === "video"
                    ? (() => {
                        switch (values.video.codec) {
                          case "vp9":
                            return <VideoParamsVp9 name="video" mode={formDisabled ? "view" : "edit"} />;
                          case "h264":
                            return <VideoParamsH264 name="video" mode={formDisabled ? "view" : "edit"} />;
                          case "vp8":
                          default:
                            return <VideoParamsVp8 name="video" mode={formDisabled ? "view" : "edit"} />;
                        }
                      })()
                    : null}
                  {tabValue === "srt" ? (
                    <SrtInputParams name="srt" mode={formDisabled ? "view" : "edit"} />
                  ) : null}
                  {tabValue === "uds" ? (
                    <UdsInputParams name="uds" mode={formDisabled ? "view" : "edit"} datagramAvailable={serverConfig.udsDatagramAvailable} />
                  ) : null}
                  {tabValue === "rtsp" ? (
                    <RtspInputParams name="rtsp" mode={formDisabled ? "view" : "edit"} />
                  ) : null}
                  {tabValue === "file" ? (
                    <Stack spacing={2}>
                      <FileInputParams name="file" mode={formDisabled ? "view" : "edit"} />
                      {renderPlaybackCacheField()}
                    </Stack>
                  ) : null}
                  {tabValue === "pcap" ? (
                    <Stack spacing={2}>
                      {captureUploadError ? <Alert severity="error">{captureUploadError}</Alert> : null}
                      {renderPcapProgress()}
                      {pcapInspectError ? <Alert severity={pcapFlows.length === 0 ? "warning" : "error"}>{pcapInspectError}</Alert> : null}
                      {pcapFlows.length > 0 ? (
                        <TextField
                          select
                          label="Captured UDP stream"
                          value={(() => {
                            const index = pcapFlows.findIndex((flow) => flow.sourceIp === values.pcap.sourceIp && flow.destinationIp === values.pcap.destinationIp && flow.sourcePort === Number(values.pcap.sourcePort) && flow.destinationPort === Number(values.pcap.destinationPort));
                            return index < 0 ? "" : index;
                          })()}
                          onChange={(event) => applyPcapFlow(pcapFlows[Number(event.target.value)])}
                          disabled={formDisabled || isPcapInspecting}
                          helperText={`${pcapFlows.length} UDP stream${pcapFlows.length === 1 ? "" : "s"} found`}
                        >
                          {pcapFlows.map((flow, index) => (
                            <MenuItem key={`${flow.sourceIp}:${flow.sourcePort}>${flow.destinationIp}:${flow.destinationPort}`} value={index}>
                              {flow.sourceIp}:{flow.sourcePort} → {flow.destinationIp}:{flow.destinationPort} ({flow.packets.toLocaleString()} packets)
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                          name="pcap.destinationIp"
                          label="Captured destination IP"
                          value={values.pcap.destinationIp}
                          onBlur={handleBlur}
                          fullWidth
                          disabled={formDisabled}
                          slotProps={{ input: { readOnly: true } }}
                          error={Boolean(getIn(touched, "pcap.destinationIp") && getIn(errors, "pcap.destinationIp"))}
                          helperText={getIn(touched, "pcap.destinationIp") ? getIn(errors, "pcap.destinationIp") : "Selected from the captured UDP stream"}
                        />
                        <TextField
                          name="pcap.destinationPort"
                          label="Captured destination port"
                          type="number"
                          value={values.pcap.destinationPort}
                          onBlur={handleBlur}
                          fullWidth
                          disabled={formDisabled}
                          slotProps={{ input: { readOnly: true } }}
                          error={Boolean(getIn(touched, "pcap.destinationPort") && getIn(errors, "pcap.destinationPort"))}
                          helperText={getIn(touched, "pcap.destinationPort") ? getIn(errors, "pcap.destinationPort") : "Selected from the captured UDP stream"}
                        />
                      </Stack>
                      {showPayloadTypeField ? (
                        <TextField
                          name="payloadType"
                          label="Payload type"
                          select
                          value={values.payloadType}
                          onChange={(event) => {
                            const nextValue = event.target.value.trim().toLowerCase();
                            setFieldValue("payloadType", nextValue);
                            if (nextValue === "data") {
                              setFieldValue("passthrough", true);
                            }
                          }}
                          onBlur={handleBlur}
                          disabled={formDisabled}
                          sx={{ width: { xs: "100%", sm: "31.5%" } }}
                        >
                          {PAYLOAD_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      <FormControlLabel control={<Switch checked={values.pcap.loop} onChange={(event) => setFieldValue("pcap.loop", event.target.checked)} disabled={formDisabled} />} label="Loop replay" />
                      {renderPlaybackCacheField()}
                    </Stack>
                  ) : null}
                  {tabValue === "flywheel" ? (
                    <FlywheelParams
 streamId={editingStream?.processors?.some(p=>p.id===values.flywheel.processorId)?editingStream.id:undefined}
 running={isEditingStreamRunning}
 canApplyLive={!configReadOnly && isEditingStreamRunning && flywheelUsable}
                      value={values.flywheel}
                      authorizedFetch={authorizedFetch}
                      onChange={(next) => {
                        if (configReadOnlyRef.current) return;
                        const normalized = next.enabled && !next.processorId ? { ...next, processorId: createFlywheelProcessorId() } : next;
                        setFieldValue("flywheel", normalized);
                      }}
                      disabled={formDisabled || (!flywheelUsable && !isPatternType)}
                      readOnly={configReadOnly}
                      readOnlyReason={flywheelReadOnlyReason}
                      referencedTargets={values.targets.filter((target) => target.inputType === "processor" && target.inputId === values.flywheel.processorId).map((target) => target.name || target.id)}
                    />
                  ) : null}
                  {tabValue === "outputs" ? (() => {
                    const trimmedPlatformId = values.platformId.trim();
                    const normalizedPlatformId = trimmedPlatformId.toLowerCase();
                    const byId = trimmedPlatformId
                      ? platformOptions.find((option) => option.id.trim() === trimmedPlatformId)
                      : undefined;
                    const byName = normalizedPlatformId
                      ? platformOptions.find(
                          (option) => option.name.trim().toLowerCase() === normalizedPlatformId
                        )
                      : undefined;
                    const resolvedPlatformName =
                      byId?.name ?? byName?.name ?? editingStream?.platformName ?? "";
                    const resolvedStreamName = values.sensor.trim() || editingStream?.sensor || "";
                    return (
                    <StreamOutputs
                      targets={values.targets}
                      onChange={(nextTargets) => { if (!configReadOnlyRef.current) setFieldValue("targets", nextTargets); }}
                      networkInterfaceOptions={networkInterfaceOptions}
                      isNetworkInterfacesLoading={isNetworkInterfacesLoading}
                      loadNetworkInterfaces={loadNetworkInterfaces}
                      disabled={formDisabled}
                      readOnly={configReadOnly}
                      platformName={resolvedPlatformName}
                      streamName={resolvedStreamName}
                      capturedDestination={
                        isPcapType && values.pcap.destinationIp && Number(values.pcap.destinationPort) > 0
                          ? {
                              ip: values.pcap.destinationIp,
                              port: Number(values.pcap.destinationPort)
                            }
                          : null
                      }
                      rawPcapEnabled={
                        isPcapType &&
                        serverConfig.rawPcapAvailable &&
                        resolveEffectiveMode().toLowerCase().includes("recast")
                      }
                      flywheelEnabled={values.flywheel.enabled && flywheelUsable}
                      flywheelProcessorId={values.flywheel.processorId}
                      showInputSelector={!isFileType}
                      udsDatagramAvailable={serverConfig.udsDatagramAvailable}
                      pcapSource={
                        isPcapType
                          ? {
                              file: values.pcap.file,
                              hostMediaDrive: values.pcap.hostMediaDrive,
                              sourceIp: values.pcap.sourceIp,
                              destinationIp: values.pcap.destinationIp,
                              sourcePort: Number(values.pcap.sourcePort) || 0,
                              destinationPort: Number(values.pcap.destinationPort) || 0
                            }
                          : null
                      }
                      authorizedFetch={authorizedFetch}
                    />
                    );
                  })() : null}
                  {tabValue === "stream" ? (
                    <Stack spacing={2}>
                      {showPayloadTypeField ? (
                        <TextField
                          name="payloadType"
                          label="Payload type"
                          select
                          value={values.payloadType}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setFieldValue(
                              "payloadType",
                              typeof nextValue === "string" ? nextValue.trim().toLowerCase() : nextValue
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            ((values.payloadType ?? "").trim().toLowerCase() === "data" && !values.passthrough) ||
                            (touched.payloadType && Boolean(errors.payloadType))
                          }
                          helperText={
                            (values.payloadType ?? "").trim().toLowerCase() === "data" && !values.passthrough
                              ? 'Payload type "Data" requires stream Passthrough'
                              : touched.payloadType
                                ? errors.payloadType
                                : undefined
                          }
                          disabled={formDisabled}
                          sx={{ width: { xs: "100%", sm: "31.5%" }, minWidth: { sm: 0 } }}
                        >
                          {PAYLOAD_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <Tooltip
                          title="Declare stream timeout. Set 0 to disable"
                          placement="top"
                          arrow
                          disableInteractive
                        >
                        <TextField
                          name="timeout"
                          label="Timeout (ms)"
                          type="number"
                          value={values.timeout}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setFieldValue("timeout", nextValue === "" ? "" : Number(nextValue));
                          }}
                          onBlur={handleBlur}
                          error={touched.timeout && Boolean(errors.timeout)}
                          helperText={touched.timeout ? errors.timeout : undefined}
                          slotProps={{ htmlInput: { min: 0, max: 3600000, step: 1 } }}
                          disabled={formDisabled}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        </Tooltip>
                        <Tooltip
                          title="Declare stream offline and restart the receiver. Set 0 to disable"
                          placement="top"
                          arrow
                          disableInteractive
                        >
                          <TextField
                            name="offlineAfter"
                            label="Offline after (ms)"
                            type="number"
                            value={values.offlineAfter}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setFieldValue("offlineAfter", nextValue === "" ? "" : Number(nextValue));
                            }}
                            onBlur={handleBlur}
                            error={touched.offlineAfter && Boolean(errors.offlineAfter)}
                            helperText={touched.offlineAfter ? errors.offlineAfter : undefined}
                            slotProps={{ htmlInput: { min: 0, max: 3600000, step: 1 } }}
                            disabled={formDisabled}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                        </Tooltip>
                        <Tooltip
                          title={BUFFER_SIZE_TOOLTIP}
                          placement="top"
                          arrow
                          disableInteractive
                        >
                          <TextField
                            name="bufferSizeKb"
                            label="Source buffer size (KB)"
                            type="number"
                            value={values.bufferSizeKb}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setFieldValue(
                                "bufferSizeKb",
                                nextValue === "" ? "" : Number(nextValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={touched.bufferSizeKb && Boolean(errors.bufferSizeKb)}
                            helperText={touched.bufferSizeKb ? errors.bufferSizeKb : undefined}
                            slotProps={{
                              htmlInput: {
                                min: DEFAULT_BUFFER_SIZE_KB,
                                max: MAX_BUFFER_SIZE_KB,
                                step: 1
                              }
                            }}
                            disabled={formDisabled}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                        </Tooltip>
                        {renderPlaybackCacheField()}
                      </Stack>
                    </Stack>
                  ) : null}
                  {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 2.5, py: 1.25, flexDirection: "column", alignItems: "stretch", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
                {isSubmitting ? (
                  <Stack spacing={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      {submitProgressText}
                    </Typography>
                    <LinearProgress />
                  </Stack>
                ) : null}
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Tooltip title="Help">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleOpenConfigStreamHelp}
                        aria-label="Open stream configuration help"
                        disabled={isSubmitting}
                      >
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      onClick={() => {
                        onClose();
                      }}
                      variant="outlined"
                      disabled={isSubmitting}
                    >
                      {configReadOnly ? "Close" : "Cancel"}
                    </Button>
                    {!configReadOnly ? <Button type="submit" disabled={submitDisabled}>
                      {isSubmitting ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} />
                          <span>{editingStream ? "Saving..." : "Adding..."}</span>
                        </Stack>
                      ) : (
                        <span>{editingStream ? "Save" : "Add stream"}</span>
                      )}
                    </Button> : null}
                  </Box>
                </Box>
              </DialogActions>
              <HostMediaFolder
                open={!configReadOnly && hostMediaDialogOpen && (isFileType || isPcapType)}
                authorizedFetch={authorizedFetch}
                initialSelectedPath={isPcapType ? values.pcap.file : values.url}
                allowedExtensions={isPcapType ? [".pcap", ".pcapng"] : [".ts", ".m2t", ".m2ts", ".mp4", ".mkv"]}
                onClose={() => setHostMediaDialogOpen(false)}
                onSelect={(selection) => {
                  if (configReadOnlyRef.current) return;
                  if (isPcapType) {
                    setFieldValue("pcap.file", selection.relPath);
                    setFieldValue("pcap.hostMediaDrive", selection.drive);
                    setFieldValue("url", `pcap:///${selection.relPath}`);
                    void inspectPcapFile(selection.relPath, selection.drive);
                  } else {
                    setFieldValue("url", buildFileUrlFromPath(selection.fullPath));
                  }
                  setHostMediaDialogOpen(false);
                }}
              />
            </Form>
          );
        }}
      </Formik>
      </DefaultPropsProvider>
    </Dialog>
  );
};

export default StreamFormDialog;
